<template>
  <div class="w-full bg-background border-b">
    <div class="px-2 py-2 sm:px-4 sm:py-4">
      <!-- Mobile Filter Trigger -->
      <div class="md:hidden flex justify-between items-center">
        <Button variant="outline" @click="isMobileFiltersOpen = true" class="h-10">
          <Filter class="h-4 w-4 mr-2" />
          Filters
          <span 
            v-if="activeFiltersCount > 0" 
            class="ml-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium"
          >
            {{ activeFiltersCount }}
          </span>
        </Button>
        <div class="flex items-center gap-2">
          <Button 
            v-if="hasActiveFilters"
            variant="outline" 
            @click="clearAllFilters"
            class="text-destructive hover:text-destructive/80 h-10"
          >
            Clear All
          </Button>
          <NuxtLink
            :to="{ path: '/cars-for-sale', query: currentFilterQuery }"
            class="px-4 py-2 h-10 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md flex items-center justify-center whitespace-nowrap transition-colors duration-200 text-sm font-medium"
          >
            Show {{ navSearchFilteredCount }} cars
          </NuxtLink>
        </div>
      </div>

      <!-- Desktop Horizontal Layout -->
      <div class="hidden md:flex items-center gap-4 w-full">
        <!-- Vehicle Types Filter -->
        <Popover>
          <PopoverTrigger as-child>
            <Button 
              variant="outline" 
              class="h-11 justify-between min-w-[160px] shrink-0 px-4"
              :class="{ 'border-primary border-2': getSelectedBodyType }"
            >
              <div class="flex flex-col items-start">
                <span class="text-xs text-muted-foreground">Type</span>
                <ClientOnly>
                  <span class="font-medium text-sm truncate">{{ getSelectedBodyType || 'All Types' }}</span>
                  <template #fallback>
                    <span class="font-medium text-sm truncate">Loading...</span>
                  </template>
                </ClientOnly>
              </div>
              <ChevronDown class="h-4 w-4 text-muted-foreground ml-3 shrink-0" />
            </Button>
          </PopoverTrigger>
        <PopoverContent class="w-[600px] p-0 bg-background border border-border/50 shadow-lg" align="start">
          <div class="grid gap-1 p-2 lg:grid-cols-[.75fr_1fr]">
            <div 
              @click="clearFilter('body')"
              class="row-span-3 flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-br from-muted/20 to-muted/40 p-4 no-underline outline-hidden focus:shadow-md hover:bg-muted/30 transition-all duration-200 border border-border/40 cursor-pointer"
            >
              <div class="mb-2 mt-4 text-lg font-medium text-foreground">
                All Types <ClientOnly><span class="text-xs text-muted-foreground">({{ bodyTypesCount }})</span><template #fallback><span class="text-xs text-muted-foreground">(loading...)</span></template></ClientOnly>
              </div>
              <p class="text-sm leading-tight text-muted-foreground">
                Browse all vehicle types
              </p>
            </div>
            <ScrollArea class="h-[300px] pr-2">
              <div 
                v-for="option in bodyTypes" 
                :key="option.value"
                @click="toggleFilterItem('body', option.value)"
                class="hover:bg-muted/50 rounded-md p-3 cursor-pointer flex justify-between items-center transition-all duration-200"
                :class="{ 'bg-muted text-primary font-semibold': isBodyTypeSelected(option.value) }"
              >
                <span class="text-foreground">{{ option.displayValue }}</span>
                <span class="bg-muted text-xs font-medium px-2 py-1 rounded-full text-muted-foreground">
                  {{ getBodyTypeCount(option.value) }}
                </span>
              </div>
            </ScrollArea>
          </div>
        </PopoverContent>
      </Popover>

        <!-- Makes Filter -->
        <Popover>
          <PopoverTrigger as-child>
            <Button 
              variant="outline" 
              class="h-11 justify-between min-w-[160px] shrink-0 px-4"
              :class="{ 'border-primary border-2': getSelectedMake }"
            >
              <div class="flex flex-col items-start">
                <span class="text-xs text-muted-foreground">Make</span>
                <span class="font-medium text-sm">{{ getSelectedMake || 'All Makes' }}</span>
              </div>
              <ChevronDown class="h-4 w-4 text-muted-foreground ml-3" />
            </Button>
          </PopoverTrigger>
         <PopoverContent class="w-[600px] p-0 bg-background border border-border/50 shadow-lg" align="start">
           <div class="p-0">
            <!-- All Makes Option -->
            <div class="p-4 border-b border-border/30">
              <div 
                 @click="clearFilter('make')"
                 class="p-3 hover:bg-muted/50 rounded-md cursor-pointer transition-all duration-200"
                 :class="{ 'bg-muted text-primary font-semibold': !getSelectedMake }"
               >
                 <div class="text-base font-medium text-foreground">All Makes <span class="text-xs text-muted-foreground">({{ makesCount }})</span></div>
                 <p class="text-sm text-muted-foreground">Browse all vehicle makes</p>
               </div>
            </div>
            
            <!-- Makes List with Checkboxes -->
            <div class="p-4 max-h-[400px] overflow-y-auto">
              <div class="grid grid-cols-2 gap-2">
                <div 
                  v-for="option in makes" 
                  :key="option.value"
                  @click="toggleFilterItem('make', option.value)"
                  class="hover:bg-muted/50 rounded-lg p-3 cursor-pointer flex items-center justify-between transition-all duration-200"
                  :class="{ 'bg-muted text-primary font-semibold': isMakeSelected(option.value) }"
                >
                  <div class="flex items-center gap-2">
                    <div class="w-6 h-6 bg-background rounded-full flex items-center justify-center overflow-hidden border border-border shrink-0">
                      <img
                        :src="typeof option.value === 'string' ? `https://driveagent.b-cdn.net/files/shared/badges/${option.value.toLowerCase()}.png` : 'https://driveagent.b-cdn.net/files/shared/badges/default.png'"
                        :alt="option.displayValue"
                        class="max-w-full max-h-full object-contain"
                        onerror="this.src='https://driveagent.b-cdn.net/files/shared/badges/default.png'; this.onerror=null;"
                      />
                    </div>
                    <span class="font-medium text-foreground">{{ option.displayValue }}</span>
                  </div>
                  <span class="bg-muted text-xs font-medium px-2 py-1 rounded-full text-muted-foreground ml-2 shrink-0">
                    {{ getMakeCount(option.value) }}
                  </span>
                </div>
              </div>
            </div>
           </div>
        </PopoverContent>
      </Popover>

        <!-- Models Filter -->
        <Popover>
          <PopoverTrigger as-child>
            <Button 
              variant="outline" 
              class="h-11 justify-between min-w-[160px] shrink-0 px-4"
              :class="{ 'border-primary border-2': getSelectedModel }"
            >
              <div class="flex flex-col items-start">
                <span class="text-xs text-muted-foreground">Model</span>
                <span class="font-medium text-sm">{{ getSelectedModel || 'All Models' }}</span>
              </div>
              <ChevronDown class="h-4 w-4 text-muted-foreground ml-3" />
            </Button>
          </PopoverTrigger>
         <PopoverContent class="w-[700px] p-0 bg-background border border-border/50 shadow-lg" align="start">
           <div class="p-0">
              
              
              
              <!-- All Models Option -->
              <div v-if="!modelSearchKeyword?.trim()" class="p-4">
                <div
                  @click="clearFilter('model'); modelSearchKeyword = ''" 
                  class="p-3 hover:bg-muted/50 rounded-md cursor-pointer transition-all duration-200"
                  :class="{ 'bg-muted text-primary font-semibold': !getSelectedModel }"
                >
                  <div class="flex items-center justify-between">
                    <div>
                      <div class="text-base font-medium text-foreground">All Models <span class="text-xs text-muted-foreground">({{ modelsCount }})</span></div>
                      <p class="text-sm text-muted-foreground">Browse all vehicle models</p>
                    </div>
                    <button 
                      v-if="getSelectedModel"
                      @click.stop="clearFilter('make'); clearFilter('model'); clearFilter('badge'); modelSearchKeyword = ''"
                      class="px-3 py-1 bg-primary text-primary-foreground text-xs rounded-md hover:bg-primary/90 transition-all"
                      aria-label="Clear all make and model filters"
                    >
                      Clear All
                    </button>
                  </div>
                </div>
              </div>

              <!-- Model Search Input (temporarily hidden) -->
              <div v-if="false" class="p-4 pt-0 border-b border-border/30">
                <div class="relative">
                  <Input
                    v-model="modelSearchKeyword"
                    class="w-full pl-9 pr-8 bg-muted/50 border-border focus:border-primary focus:ring-primary rounded-lg transition-all duration-200 h-9 mb-2 text-foreground placeholder:text-muted-foreground"
                    placeholder="Search models..."
                    @update:modelValue="(value: string | number) => { modelSearchKeyword = String(value); debounceModelSearch(); }"
                    @keyup.enter="handleModelSearch"
                  />
                  <span class="absolute start-0 inset-y-0 flex items-center justify-center px-2">
                    <Search class="size-5 text-muted-foreground" />
                  </span>
                  <div v-if="modelSearchKeyword" class="absolute inset-y-0 right-2 flex items-center">
                    <button
                      @click="() => { 
                        modelSearchKeyword = ''; 
                        clearFilter('model');
                        // Reset expanded makes to show all models
                        expandedMakes = [];
                      }"
                      type="button"
                      class="text-muted-foreground hover:text-foreground transition-colors duration-200 focus:outline-none"
                      aria-label="Clear search"
                    >
                      <X class="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>

              <!-- Two-column Layout for Make/Model Navigation -->
              <div class="flex h-[400px]">
                <!-- Left column (Makes) -->
                <div class="w-[30%] md:w-[40%] lg:w-[200px] border-r border-border/30 overflow-y-auto">
                  <div 
                    v-for="make in getFilteredMakes()" 
                    :key="make.value"
                    class="transition-colors duration-200"
                  >
                                          <div 
                      class="px-2 sm:px-3 md:px-4 py-2 md:py-3 flex justify-between items-center hover:bg-muted/50 group relative mb-1 cursor-pointer"
                      :class="{
                        'bg-muted/30 font-semibold': isGroupExpanded(make.displayValue),
                        'border-l-4 border-primary bg-muted text-primary font-semibold': isMakeSelected(make.value)
                      }"
                      @click="toggleFilterItem('make', make.value)"
                    >
                      <div class="flex items-center gap-2">
                        <!-- Checkbox for make selection -->
                        <div 
                          class="flex items-center justify-center h-4 w-4 rounded border pointer-events-none"
                          :class="isMakeSelected(make.value) ? 'bg-primary border-primary' : 'border-border'"
                        >
                          <svg v-if="isMakeSelected(make.value)" class="h-3 w-3 text-primary-foreground" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                          </svg>
                        </div>
                        
                        <div 
                          class="flex items-center gap-2 flex-grow" 
                          @click.stop="toggleMakeExpanded(make.displayValue); toggleFilterItem('make', make.value)"
                        >
                          <div class="w-5 h-5 bg-background rounded-full flex items-center justify-center overflow-hidden border border-border shrink-0">
                            <img
                              :src="typeof make.value === 'string' ? `https://driveagent.b-cdn.net/files/shared/badges/${make.value.toLowerCase()}.png` : 'https://driveagent.b-cdn.net/files/shared/badges/default.png'"
                              :alt="make.displayValue"
                              class="max-w-full max-h-full object-contain"
                              onerror="this.src='https://driveagent.b-cdn.net/files/shared/badges/default.png'; this.onerror=null;"
                            />
                          </div>
                          <span class="text-sm text-foreground">{{ make.displayValue }}</span>
                        </div>
                      </div>
                      <div class="flex items-center">
                        <span class="bg-muted text-xs font-medium px-2 py-1 rounded-full text-muted-foreground pointer-events-none">
                          {{ typeof make.value === 'string' ? getMakeCount(make.value) : 0 }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Right column (Models for Selected Make) -->
                <div class="flex-1 overflow-y-auto p-4 bg-background">
                  <!-- Global Search Results (if keyword exists) -->
                  <div v-if="modelSearchKeyword?.trim()">
                    <div class="py-1 px-3 text-sm font-medium text-muted-foreground border-b border-border/30 mb-2">
                      Search Results for "{{ modelSearchKeyword.trim() }}"
                      <span class="ml-2 text-xs px-1.5 py-0.5 bg-muted text-foreground rounded-full">
                        {{ globallyFilteredModels.length }}
                      </span>
                    </div>
                    
                    <div v-if="globallyFilteredModels.length > 0" class="space-y-1">
                      <div
                        v-for="model in globallyFilteredModels"
                        :key="model.value"
                        @click="toggleFilterItem('model', model.value)"
                        class="py-2 px-3 rounded-md cursor-pointer group transition-all duration-200"
                        :class="{ 
                          'bg-muted text-primary font-semibold': isModelSelected(model.value),
                          'hover:bg-muted/50': !isModelSelected(model.value)
                        }"
                      >
                        <div class="flex items-center justify-between">
                          <div>
                            <span class="font-medium text-sm text-foreground">{{ model.displayValue }}</span>
                            <p class="text-xs text-foreground/70 group-hover:text-foreground" :class="{'text-foreground': isModelSelected(model.value)}">{{ model.displayMake || '' }}</p>
                          </div>
                          <span 
                            class="text-xs font-medium px-2 py-1 rounded-full ml-2 bg-muted text-muted-foreground"
                          >
                            {{ getModelCount(model.value) }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-sm text-muted-foreground py-6 px-3 text-center">
                      <div class="flex flex-col items-center justify-center">
                        <div class="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-2">
                          <Search class="h-6 w-6 text-muted-foreground" />
                        </div>
                        <p>No matching models found.</p>
                        <p class="text-xs text-muted-foreground mt-1">Try a different search term.</p>
                      </div>
                    </div>
                  </div>

                  <!-- Models for Selected Make/Makes -->
                  <div v-else>
                    <!-- When makes are selected but no specific make is expanded -->
                    <div v-if="isSelectedMakesMode" class="space-y-6">
                      <template v-for="make in getSelectedMakes()" :key="make.value">
                        <div class="mb-4">
                          <div class="pb-2 mb-2 border-b border-gray-100">
                            <div class="font-medium text-gray-700">{{ make.displayValue }} Models 
                              <span class="text-xs text-gray-500">
                                ({{ typeof make.value === 'string' 
                                   ? ((modelsForExpandedMakes[make.value.toLowerCase()] || []).filter(model => getModelCount(model.value) > 0).length || 0) 
                                   : 0 }})
                              </span>
                            </div>
                          </div>
                          
                          <div v-if="typeof make.value === 'string' && 
                                    (modelsForExpandedMakes[make.value.toLowerCase()] || []).filter(model => getModelCount(model.value) > 0).length > 0"
                               class="space-y-1">
                            <div
                              v-for="model in (modelsForExpandedMakes[make.value.toLowerCase()] || []).filter(model => getModelCount(model.value) > 0)"
                              :key="model.value"
                              @click="toggleFilterItem('model', model.value)"
                              class="py-2 px-3 rounded-md cursor-pointer group transition-all duration-200 dark:text-gray-100"
                              :class="{ 
                                'bg-gray-200 text-gray-800 font-semibold dark:bg-neutral-700 dark:text-gray-100': isModelSelected(model.value),
                                'hover:bg-gray-50 dark:hover:bg-neutral-800': !isModelSelected(model.value)
                              }"
                            >
                              <div class="flex items-center justify-between">
                                <div>
                                  <span class="font-medium text-sm">{{ getModelDisplayName(model.value) }}</span>
                                  <p class="text-xs text-foreground/70 group-hover:text-foreground" :class="{'text-foreground': isModelSelected(model.value)}">{{ getModelBodyType(model.value) }}</p>
                                </div>
                                <span 
                                  class="text-xs font-medium px-2 py-1 rounded-full ml-2"
                                  :class="{ 
                                    'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100': isModelSelected(model.value),
                                    'bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-200': !isModelSelected(model.value)
                                  }"
                                >
                                  {{ getModelCount(model.value) }}
                                </span>
                              </div>

                              <!-- Badge selection section - appears for selected model -->
                              <div 
                                v-if="isModelSelected(model.value)"
                                class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/30"
                              >
                                <div class="flex items-center justify-between mb-2">
                                  <span class="text-xs font-medium text-primary flex items-center">
                                    <span class="mr-1"></span> Refine by Badge:
                                    <span v-if="availableBadgesForModel(model.value).length === 0" class="ml-1 italic text-gray-400">
                                      (No badges found)
                                    </span>
                                  </span>
                                  <button 
                                    v-if="selectedBadges.length > 0"
                                    @click.stop="clearBadgesForModel(model.value)"
                                    class="text-xs text-gray-700 hover:underline dark:text-gray-300"
                                  >
                                    Clear badges
                                  </button>
                                </div>
                                <div v-if="availableBadgesForModel(model.value).length > 0" class="flex flex-wrap gap-2">
                                  <button
                                    v-for="badge in availableBadgesForModel(model.value)"
                                    :key="badge.value"
                                    @click.stop="toggleFilterItem('badge', badge.value)"
                                    class="px-2 py-1 text-xs rounded-full transition-all flex items-center"
                                    :class="isBadgeSelected(badge.value) ? 
                                      'bg-neutral-800 text-white dark:text-gray-900 dark:bg-gray-100' : 
                                      'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-neutral-700 dark:text-gray-200 dark:hover:bg-gray-600'"
                                  >
                                    <span v-if="isBadgeSelected(badge.value)" class="mr-1">✓</span>
                                    {{ badge.displayValue }}
                                    <span class="ml-1 text-xs opacity-70">({{ badge.count }})</span>
                                  </button>
                                </div>
                                <div v-else class="text-xs text-gray-400 italic">
                                  No badges available for this model
                                </div>
                              </div>
                            </div>
                          </div>
                          <div v-else class="py-6 text-sm text-gray-500 italic text-center">
                            No models available for this make.
                          </div>
                        </div>
                      </template>
                    </div>
                    
                    <!-- When a specific make is expanded through UI interaction -->
                    <template v-else-if="expandedMakes.length > 0">
                      <div v-for="makeName in expandedMakes" :key="makeName">
                        <div class="pb-2 mb-3 border-b border-gray-100 dark:border-gray-800/30">
                          <div class="font-medium text-gray-700 dark:text-gray-200">{{ makeName }} Models 
                            <span class="text-xs text-gray-500 dark:text-gray-400">
                              ({{ getModelsCountForMake(makeName) }})
                            </span>
                          </div>
                        </div>
                        
                        <template v-if="getModelsCountForMake(makeName) > 0">
                          <div class="space-y-1">
                            <div
                              v-for="model in getModelsForMake(makeName)"
                              :key="model.value"
                              @click="toggleFilterItem('model', model.value)"
                              class="py-2 px-3 rounded-md cursor-pointer group transition-all duration-200 dark:text-gray-100"
                              :class="{ 
                                'bg-gray-200 text-gray-800 font-semibold dark:bg-neutral-700 dark:text-gray-100': isModelSelected(model.value),
                                'hover:bg-gray-50 dark:hover:bg-neutral-800': !isModelSelected(model.value)
                              }"
                            >
                              <div class="flex items-center justify-between">
                                <div>
                                  <span class="font-medium text-sm">{{ getModelDisplayName(model.value) }}</span>
                                  <p class="text-xs text-foreground/70 group-hover:text-foreground" :class="{'text-foreground': isModelSelected(model.value)}">{{ getModelBodyType(model.value) }}</p>
                                </div>
                                <span 
                                  class="text-xs font-medium px-2 py-1 rounded-full ml-2"
                                  :class="{ 
                                    'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100': isModelSelected(model.value),
                                    'bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-200': !isModelSelected(model.value)
                                  }"
                                >
                                  {{ getModelCount(model.value) }}
                                </span>
                              </div>

                              <!-- Badge selection section - appears for selected model -->
                              <div 
                                v-if="isModelSelected(model.value)"
                                class="mt-3 pt-3 border-t border-gray-100"
                              >
                                <div class="flex items-center justify-between mb-2">
                                  <span class="text-xs font-medium text-gray-500 flex items-center">
                                    <span class="mr-1"></span> Refine by Badge:
                                    <span v-if="availableBadgesForModel(model.value).length === 0" class="ml-1 italic text-gray-400">
                                      (No badges found)
                                    </span>
                                  </span>
                                  <button 
                                    v-if="selectedBadges.length > 0"
                                    @click.stop="clearBadgesForModel(model.value)"
                                    class="text-xs text-gray-700 hover:underline dark:text-gray-300"
                                  >
                                    Clear badges
                                  </button>
                                </div>
                                <div v-if="availableBadgesForModel(model.value).length > 0" class="flex flex-wrap gap-2">
                                  <button
                                    v-for="badge in availableBadgesForModel(model.value)"
                                    :key="badge.value"
                                    @click.stop="toggleFilterItem('badge', badge.value)"
                                    class="px-2 py-1 text-xs rounded-full transition-all flex items-center"
                                    :class="isBadgeSelected(badge.value) ? 
                                      'bg-neutral-800 text-white dark:bg-gray-600' : 
                                      'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-neutral-700 dark:text-gray-200 dark:hover:bg-gray-600'"
                                  >
                                    <span v-if="isBadgeSelected(badge.value)" class="mr-1">✓</span>
                                    {{ badge.displayValue }}
                                    <span class="ml-1 text-xs opacity-70">({{ badge.count }})</span>
                                  </button>
                                </div>
                                <div v-else class="text-xs text-gray-400 italic">
                                  No badges available for this model
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                        <template v-else>
                          <div class="py-6 text-sm text-gray-500 italic text-center dark:text-gray-400">
                            <div class="flex flex-col items-center justify-center">
                              <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-2 dark:bg-neutral-800">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400"><circle cx="12" cy="12" r="10"></circle><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"></line></svg>
                              </div>
                              <p>No models available for this make.</p>
                              <p class="text-xs text-gray-400 mt-1">Try selecting a different body type or make.</p>
                            </div>
                          </div>
                        </template>
                      </div>
                    </template>
                    <!-- Show all models when no makes are selected or expanded -->
                    <div v-else class="space-y-4">
                      <div class="pb-2 border-b border-gray-100">
                        <div class="font-medium text-primary flex items-center">
                          <span>All Available Models</span>
                          <span class="ml-2 text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-200">
                            {{ models.filter(model => getModelCount(model.value) > 0).length }}
                          </span>
                        </div>
                      </div>
                      
                      <!-- Show popular/top models first section -->
                      <div v-if="frontPageStore.popularModels?.length > 0" class="mb-6">
                        <h3 class="text-sm font-medium text-muted-foreground mb-2">Popular Models</h3>
                        <div class="space-y-1">
                          <div
                            v-for="model in frontPageStore.popularModels.slice(0, 5)"
                            :key="model.value"
                            @click="toggleFilterItem('model', model.value)"
                            class="py-2 px-3 rounded-md cursor-pointer group transition-all border border-border/90 duration-200"
                            :class="{ 
                              'bg-muted text-primary font-semibold': isModelSelected(model.value),
                              'hover:bg-muted/50': !isModelSelected(model.value)
                            }"
                          >
                            <div class="flex items-center justify-between">
                              <div>
                                <span class="font-medium text-sm">{{ getModelDisplayName(model.value) }}</span>
                                <p class="text-xs text-foreground/70 group-hover:text-foreground" :class="{'text-foreground': isModelSelected(model.value)}">
                                  {{ getModelMakeName(model.value) }} · {{ getModelBodyType(model.value) }}
                                </p>
                              </div>
                              <span 
                                class="text-xs font-medium px-2 py-1 rounded-full ml-2 bg-muted text-muted-foreground"
                              >
                                {{ getModelCount(model.value) }}
                              </span>
                            </div>

                              <!-- Badge selection for Popular Models -->
                              <div 
                                v-if="isModelSelected(model.value)"
                                class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/30"
                              >
                                <div class="flex items-center justify-between mb-2">
                                  <span class="text-xs font-medium text-primary flex items-center">
                                    <span class="mr-1"></span> Refine by Badge:
                                    <span v-if="availableBadgesForModel(model.value).length === 0" class="ml-1 italic text-gray-400">
                                      (No badges found)
                                    </span>
                                  </span>
                                  <button 
                                    v-if="selectedBadges.length > 0"
                                    @click.stop="clearBadgesForModel(model.value)"
                                    class="text-xs text-gray-700 hover:underline dark:text-gray-300"
                                  >
                                    Clear badges
                                  </button>
                                </div>
                                <div v-if="availableBadgesForModel(model.value).length > 0" class="flex flex-wrap gap-2">
                                  <button
                                    v-for="badge in availableBadgesForModel(model.value)"
                                    :key="badge.value"
                                    @click.stop="toggleFilterItem('badge', badge.value)"
                                    class="px-2 py-1 text-xs rounded-full transition-all flex items-center"
                                    :class="isBadgeSelected(badge.value) ? 
                                      'bg-neutral-800 text-white dark:text-gray-900 dark:bg-gray-100' : 
                                      'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-neutral-700 dark:text-gray-200 dark:hover:bg-gray-600'"
                                  >
                                    <span v-if="isBadgeSelected(badge.value)" class="mr-1">✓</span>
                                    {{ badge.displayValue }}
                                    <span class="ml-1 text-xs opacity-70">({{ badge.count }})</span>
                                  </button>
                                </div>
                                <div v-else class="text-xs text-gray-400 italic">
                                  No badges available for this model
                                </div>
                              </div>
                          </div>
                        </div>
                      </div>
                      
                      <!-- Group models by make -->
                      <div v-if="makes.length > 0" class="space-y-6">
                        <div class="border-b border-border/30 my-4"></div>
                        <h3 class="text-sm font-medium text-muted-foreground pb-2">All Makes ({{ makes.length }})</h3>
                        <template v-for="make in makes.sort((a, b) => 
                          String(a.displayValue || '').localeCompare(String(b.displayValue || ''))
                        )" :key="make.value">
                          <div v-if="typeof make.value === 'string' && 
                                     (modelsForExpandedMakes[make.value.toLowerCase()] || []).filter(model => getModelCount(model.value) > 0).length > 0"
                               class="mb-4 p-3 border border-border/90 rounded-lg">
                            <div class="flex items-center mb-2">
                              <div class="w-5 h-5 bg-background rounded-full flex items-center justify-center overflow-hidden border border-border mr-2">
                                <img
                                  :src="typeof make.value === 'string' ? `https://driveagent.b-cdn.net/files/shared/badges/${make.value.toLowerCase()}.png` : 'https://driveagent.b-cdn.net/files/shared/badges/default.png'"
                                  :alt="make.displayValue"
                                  class="max-w-full max-h-full object-contain"
                                  onerror="this.src='https://driveagent.b-cdn.net/files/shared/badges/default.png'; this.onerror=null;"
                                />
                              </div>
                              <h3 class="text-sm font-medium text-muted-foreground">{{ make.displayValue }}</h3>
                              <span class="ml-2 text-xs px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                                {{ typeof make.value === 'string' ? (modelsForExpandedMakes[make.value.toLowerCase()] || []).filter(model => getModelCount(model.value) > 0).length : 0 }}
                              </span>
                            </div>
                            
                            <div class="space-y-1">
                              <div
                                v-for="model in (modelsForExpandedMakes[make.value.toLowerCase()] || []).filter(model => getModelCount(model.value) > 0)"
                                :key="model.value"
                                @click="toggleFilterItem('model', model.value)"
                                class="py-2 px-3 rounded-md cursor-pointer group transition-all duration-200 dark:text-gray-100"
                                :class="{ 
                                  'bg-gray-200 text-gray-800 font-semibold dark:bg-neutral-700 dark:text-gray-100': isModelSelected(model.value),
                                  'hover:bg-gray-50 dark:hover:bg-neutral-800': !isModelSelected(model.value)
                                }"
                              >
                                <div class="flex items-center justify-between">
                                  <div>
                                    <span class="font-medium text-sm">{{ getModelDisplayName(model.value) }}</span>
                                    <p class="text-xs text-foreground/70 group-hover:text-foreground" :class="{'text-foreground': isModelSelected(model.value)}">{{ getModelBodyType(model.value) }}</p>
                                  </div>
                                  <span 
                                                                        class="text-xs font-medium px-2 py-1 rounded-full ml-2"
                                    :class="{ 
                                     'bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-100': isModelSelected(model.value),
                                     'bg-gray-100 text-gray-600 dark:bg-neutral-700 dark:text-gray-200': !isModelSelected(model.value)
                                   }"
                                 >
                                 
                                    {{ getModelCount(model.value) }}
                                  </span>
                                </div>

                                <!-- Badge selection section - appears for selected model (All Makes view) -->
                                <div 
                                  v-if="isModelSelected(model.value)"
                                  class="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700/30"
                                >
                                  <div class="flex items-center justify-between mb-2">
                                    <span class="text-xs font-medium text-primary flex items-center">
                                      <span class="mr-1"></span> Refine by Badge:
                                      <span v-if="availableBadgesForModel(model.value).length === 0" class="ml-1 italic text-gray-400">
                                        (No badges found)
                                      </span>
                                    </span>
                                    <button 
                                      v-if="selectedBadges.length > 0"
                                      @click.stop="clearBadgesForModel(model.value)"
                                      class="text-xs text-gray-700 hover:underline dark:text-gray-300"
                                    >
                                      Clear badges
                                    </button>
                                  </div>
                                  <div v-if="availableBadgesForModel(model.value).length > 0" class="flex flex-wrap gap-2">
                                    <button
                                      v-for="badge in availableBadgesForModel(model.value)"
                                      :key="badge.value"
                                      @click.stop="toggleFilterItem('badge', badge.value)"
                                      class="px-2 py-1 text-xs rounded-full transition-all flex items-center"
                                      :class="isBadgeSelected(badge.value) ? 
                                        'bg-neutral-800 text-white dark:bg-gray-600' : 
                                        'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-neutral-700 dark:text-gray-200 dark:hover:bg-gray-600'"
                                    >
                                      <span v-if="isBadgeSelected(badge.value)" class="mr-1">✓</span>
                                      {{ badge.displayValue }}
                                      <span class="ml-1 text-xs opacity-70">({{ badge.count }})</span>
                                    </button>
                                  </div>
                                  <div v-else class="text-xs text-gray-400 italic">
                                    No badges available for this model
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
         </PopoverContent>
      </Popover>

        <!-- Search Input with Suggestions -->
        <div class="relative flex-1 max-w-xl">
          <div class="relative">
            <Input 
              v-model="searchKeyword" 
              class="w-full pl-11 pr-11 h-11 border-input focus:border-primary focus:ring-1 focus:ring-primary text-sm placeholder:text-muted-foreground"
              placeholder="Search by make, model, color, features, price..." 
              @input="handleSearchInputWithSuggestions"
              @keydown="handleKeyDown"
              @keyup.enter="applyKeywordSearch"
              @focus="showSuggestions = true; getSuggestions(searchKeyword || '')"
              @blur="handleSearchBlur"
              autocomplete="off"
            />
            <div class="absolute left-0 inset-y-0 flex items-center justify-center pl-4">
              <Search class="h-4 w-4 text-muted-foreground" />
            </div>
            <div v-if="searchKeyword" class="absolute inset-y-0 right-3 flex items-center">
              <button
                @click="clearSearchWithSuggestions"
                type="button"
                class="text-muted-foreground hover:text-foreground transition-colors duration-200 focus:outline-none p-1 rounded-sm hover:bg-accent"
                aria-label="Clear search"
              >
                <X class="h-4 w-4" />
              </button>
            </div>
            
            <!-- Enhanced Keyword Suggestions Dropdown -->
            <div 
              v-if="showSuggestions && keywordSuggestions.length > 0" 
              class="absolute top-full left-0 right-0 mt-1 bg-background border border-border/50 rounded-lg shadow-xl z-50 max-h-80 overflow-hidden"
            >
              <div class="p-0">
                <!-- Header -->
                <div class="px-4 py-3 border-b border-border/30 bg-muted/30">
                  <div class="flex items-center justify-between">
                    <div class="text-xs font-medium text-muted-foreground">
                      {{ searchKeyword ? 'Suggestions' : 'Search History & Popular' }}
                    </div>
                    <div v-if="!searchKeyword && (hasRecentSearches || hasPopularSearches)" class="text-xs text-muted-foreground">
                      Recent & Popular
                    </div>
                  </div>
                </div>

                <!-- Suggestions List -->
                <div class="max-h-60 overflow-y-auto">
                  <div
                    v-for="(suggestion, index) in keywordSuggestions"
                    :key="`${suggestion.text}-${suggestion.category}-${index}`"
                    @mousedown="selectSuggestion(suggestion.text)"
                    @mouseenter="currentSuggestionIndex = index"
                    class="group flex items-center justify-between px-4 py-3 cursor-pointer transition-all duration-150 border-b border-border/20 last:border-b-0"
                    :class="{
                      // Keyboard navigation highlight (highest priority)
                      'bg-muted text-primary': currentSuggestionIndex === index,
                      // Hover states (only when not keyboard selected)
                      'hover:bg-muted/50': currentSuggestionIndex !== index,
                      // Priority-based backgrounds (only when not selected)
                      'bg-gradient-to-r from-muted/30 to-transparent': (suggestion as any).priority === 'high' && currentSuggestionIndex !== index,
                      'bg-gradient-to-r from-primary/10 to-transparent': (suggestion as any).category === 'popular' && currentSuggestionIndex !== index,
                      'bg-gradient-to-r from-muted/20 to-transparent': (suggestion as any).category === 'recent' && currentSuggestionIndex !== index
                    }"
                  >
                    <div class="flex items-center gap-3 flex-1 min-w-0">
                      <!-- Icon -->
                      <div class="flex-shrink-0 w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                        <component 
                          :is="getIconComponentForSuggestion(suggestion)" 
                          class="w-4 h-4 text-muted-foreground"
                        />
                      </div>
                      
                      <!-- Content -->
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                          <span class="font-medium text-foreground truncate">
                            {{ suggestion.text }}
                          </span>
                          
                          <!-- Category Badge -->
                          <span 
                            class="text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0"
                            :class="getSuggestionCategoryStyle(suggestion.category)"
                          >
                            {{ getSuggestionCategoryLabel(suggestion.category) }}
                          </span>
                        </div>
                        
                        <!-- Description -->
                        <div v-if="(suggestion as any).description" class="text-xs text-muted-foreground mt-0.5 truncate">
                          {{ (suggestion as any).description }}
                        </div>
                      </div>
                    </div>
                    
                    <!-- Match Count or Action -->
                    <div class="flex-shrink-0 flex items-center gap-2">
                      <span 
                        v-if="suggestion.matchCount && (suggestion as any).category !== 'recent' && (suggestion as any).category !== 'popular'" 
                        class="text-xs font-medium text-muted-foreground bg-muted px-2 py-1 rounded-full"
                      >
                        {{ suggestion.matchCount }} {{ suggestion.matchCount === 1 ? 'car' : 'cars' }}
                      </span>
                      
                      <!-- Priority Indicator for High Priority Items -->
                      <div 
                        v-if="(suggestion as any).priority === 'high' && (suggestion as any).category !== 'recent' && (suggestion as any).category !== 'popular'"
                        class="w-2 h-2 bg-primary rounded-full"
                        title="Popular choice"
                      ></div>
                      
                      <!-- Arrow for better UX -->
                      <svg class="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
                      </svg>
                    </div>
                  </div>
                </div>

                <!-- Footer with Quick Actions (if no search term) -->
                <div 
                  v-if="!searchKeyword && (hasRecentSearches || hasPopularSearches)" 
                  class="px-4 py-2 border-t border-border/30 bg-muted/30"
                >
                  <div class="flex items-center justify-between text-xs">
                    <div class="text-muted-foreground">
                      Start typing to search vehicles...
                    </div>
                    <button 
                      v-if="hasRecentSearches"
                      @click="clearRecentSearches(); getSuggestions('')"
                      class="text-muted-foreground hover:text-foreground underline"
                    >
                      Clear recent
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center gap-3">
          <!-- Clear Filters Button -->
          <!-- <Button 
            v-if="hasActiveFilters"
            variant="ghost" 
            @click="clearAllFilters"
            class="h-11 px-4 text-muted-foreground hover:text-foreground"
          >
            Clear All
          </Button>
           -->
          <!-- Filter button FOR DESKTOP (opens Dialog) -->
          <Button 
            variant="outline" 
            @click="isFilterDialogOpen = true"
            class="h-11 px-4 relative"
          >
            <Filter class="h-4 w-4 mr-2" />
       
            <span 
              v-if="activeFiltersCount > 0" 
              class="ml-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium"
            >
              {{ activeFiltersCount }}
            </span>
          </Button>
          
          <!-- Show results button -->
          <NuxtLink
            :to="{ path: '/cars-for-sale', query: currentFilterQuery }"
            class="h-11 px-6 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md flex items-center justify-center whitespace-nowrap transition-colors duration-200 font-medium text-sm shadow-sm"
          >
            Show {{ navSearchFilteredCount }} cars
          </NuxtLink>
        </div>
      </div>
    </div>

  <!-- Mobile Filter Drawer (use the new component) -->
  <MobileFilterSheet
    :open="isMobileFiltersOpen"
    @update:open="isMobileFiltersOpen = $event"
    :activeFiltersCount="activeFiltersCount"
    :navSearchFilteredCount="navSearchFilteredCount"
    :currentFilterQuery="currentFilterQuery"
    
    :bodyTypes="bodyTypes"
    :bodyTypesCount="bodyTypesCount"
    :getSelectedBodyType="getSelectedBodyType || ''"
    :isBodyTypeSelected="isBodyTypeSelected"
    :getBodyTypeCount="getBodyTypeCount"

    :makes="makes"
    :makesCount="makesCount"
    :getSelectedMake="getSelectedMake || ''"
    :isMakeSelected="isMakeSelected"
    :getMakeCount="getMakeCount"

    :modelsCount="modelsCount"
    :getSelectedModel="getSelectedModel || ''"
    :isModelSelected="isModelSelected"
    :getModelCount="getModelCount"
    :getModelDisplayName="(value: string) => getModelDisplayName(value) || ''"
    :getModelBodyType="getModelBodyType"
    :globallyFilteredModels="globallyFilteredModels"
    :modelsForExpandedMakes="modelsForExpandedMakes"
    :selectedMakesForMobileModelList="getSelectedMakes()" 
    :allMakesForMobileModelList="makes.sort((a, b) => String(a.displayValue || '').localeCompare(String(b.displayValue || '')))"

    :searchKeyword="searchKeyword"
    @update:searchKeyword="searchKeyword = $event; debounceSearch()" 
    :modelSearchKeyword="modelSearchKeyword"
    @update:modelSearchKeyword="modelSearchKeyword = $event; debounceModelSearch()"

    :frontPageFiltersModel="frontPageStore.frontPageFilters.model"
    :availableBadgesForModel="availableBadgesForModel"
    :selectedBadges="selectedBadges"
    :isBadgeSelected="isBadgeSelected"

    @toggle-filter="(payload) => toggleFilterItem(payload.filterName, payload.itemValue)"
    @clear-filter="(filterName) => clearFilter(filterName)"
    @apply-keyword-search="applyKeywordSearch"
    @handle-model-search="handleModelSearch"
  />

  <!-- Filter Dialog -->
  <Dialog :open="isFilterDialogOpen" @update:open="isFilterDialogOpen = $event">
    <DialogContent class="sm:max-w-[425px] p-0 overflow-hidden bg-background">
      <div class="p-6 pb-4">
        <DialogTitle class="flex items-center text-xl font-semibold mb-1 text-foreground">
          <Filter class="h-5 w-5 mr-2 text-muted-foreground" />
          Active Filters
          <span 
            v-if="activeFiltersCount > 1" 
            class="ml-2 bg-primary text-primary-foreground text-xs rounded-full h-5 w-5 flex items-center justify-center"
          >
            {{ activeFiltersCount }}
          </span>
        </DialogTitle>
        <DialogDescription class="text-muted-foreground text-sm">
          View and manage your currently applied filters.
        </DialogDescription>
      </div>
      
      <div class="px-6 py-2 max-h-[60vh] overflow-y-auto">
        <div v-if="Object.keys(groupedFilters).length === 0" class="text-center py-4 text-muted-foreground">
          No filters currently active.
        </div>
        
        <!-- Group by filter category -->
        <div v-for="(items, category) in groupedFilters" :key="category" class="mb-6">
          <h3 class="text-base font-semibold text-foreground mb-2 flex items-center">
            {{ getCategoryLabel(category) }}
            <span class="ml-2 text-xs font-medium text-muted-foreground">({{ items.length }})</span>
          </h3>
          
          <!-- Search Keywords special case -->
          <div v-if="category === 'search'" class="bg-muted/50 hover:bg-muted transition-colors duration-200 rounded-lg mb-2 flex items-center justify-between p-3">
            <span class="text-foreground">"{{ items[0] }}"</span>
            <button 
              @click="clearFilter('search_keywords')"
              class="text-muted-foreground hover:text-foreground focus:outline-none hover:bg-muted rounded-full p-1 transition-colors duration-200"
              aria-label="Remove filter"
            >
              <X class="h-4 w-4" />
            </button>
          </div>
          
          <!-- Multiple selection items -->
          <div 
            v-else
            v-for="item in items" 
            :key="`${category}-${item}`"
            class="bg-muted/50 hover:bg-muted transition-colors duration-200 rounded-lg mb-2 flex items-center justify-between p-3"
          >
            <div class="flex items-center">
              <!-- Add icons or visual indicators based on category -->
              <div v-if="category === 'make'" class="w-6 h-6 bg-background rounded-full flex items-center justify-center overflow-hidden border border-border mr-2">
                <img
                  :src="typeof item === 'string' ? `https://driveagent.b-cdn.net/files/shared/badges/${item.toLowerCase()}.png` : 'https://driveagent.b-cdn.net/files/shared/badges/default.png'"
                  :alt="getDisplayValue(category, item)"
                  class="max-w-full max-h-full object-contain"
                  onerror="this.src='https://driveagent.b-cdn.net/files/shared/badges/default.png'; this.onerror=null;"
                />
              </div>
              <span class="text-foreground">{{ getDisplayValue(category, item) }}</span>
            </div>
            <button 
              @click="removeFilterItem(category, item)"
              class="text-muted-foreground hover:text-foreground focus:outline-none hover:bg-muted rounded-full p-1 transition-colors duration-200" 
              aria-label="Remove filter"
            >
              <X class="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      
      <div class="flex justify-end items-center gap-2 p-4 border-t border-border/30">
        <Button 
          variant="outline" 
          @click="clearAllFilters"
          class="text-destructive border-border hover:bg-muted"
        >
          Clear All
        </Button>
        <Button variant="default" @click="isFilterDialogOpen = false" class="bg-primary text-primary-foreground hover:bg-primary/90">
          Close
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</div>
</template>


<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useRoute, useRouter } from '#imports'
import { useVehiclesStore } from '~/stores/vehicles'
import { useFrontPageStore, type FrontPageFilters } from '~/stores/frontPage'
import { ChevronDown, Search, Filter, X, Car, Truck, Palette, Fuel, Settings, DollarSign, Calendar, Clock, TrendingUp, Link, Zap, Users, MapPin, Award, Wrench } from 'lucide-vue-next'
import { Input } from '~/layers/base/components/ui/input'
import { Button } from '~/layers/base/components/ui/button'
import { Label } from '~/layers/base/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '~/layers/base/components/ui/popover'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetFooter, SheetTrigger } from '~/layers/base/components/ui/sheet'
import { ScrollArea } from '~/layers/base/components/ui/scroll-area'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '~/layers/base/components/ui/collapsible'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/layers/base/components/ui/dialog'
import type { FilterOption } from '../../../types'
import { useForm } from 'vee-validate'
import MobileFilterSheet from '~/layers/base/components/MobileFilterSheet.vue' // Import the new component

// Import keyword suggestions composable
const { 
  suggestions: keywordSuggestions, 
  loading: suggestionsLoading, 
  getSuggestions, 
  clearSuggestions 
} = useKeywordSuggestions()

const vehiclesStore = useVehiclesStore()
const frontPageStore = useFrontPageStore()

// Keyword suggestions state
const showSuggestions = ref(false)

// Enhanced keyword suggestions functions with autocomplete
const currentSuggestionIndex = ref(-1)
const isAutocompleting = ref(false)

const handleSearchInputWithSuggestions = (event: Event | string) => {
  // Properly extract the value from the input event
  let value: string = ''
  
  if (typeof event === 'string') {
    value = event
  } else if (event && event.target && 'value' in event.target) {
    value = (event.target as HTMLInputElement).value
  } else {
    value = ''
  }
  
  searchKeyword.value = value
  currentSuggestionIndex.value = -1 // Reset selection
  
  // Get suggestions if there's text
  if (value && value.trim().length > 0) {
    getSuggestions(value.trim())
    showSuggestions.value = true
  } else {
    // Show recent/popular searches when field is empty
    getSuggestions('')
    showSuggestions.value = true
  }
  
  // Also trigger the regular debounced search
  debounceSearch()
}

// Handle keyboard navigation in suggestions
const handleKeyDown = (event: KeyboardEvent) => {
  if (!showSuggestions.value || keywordSuggestions.value.length === 0) return
  
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      currentSuggestionIndex.value = Math.min(
        currentSuggestionIndex.value + 1,
        keywordSuggestions.value.length - 1
      )
      break
      
    case 'ArrowUp':
      event.preventDefault()
      currentSuggestionIndex.value = Math.max(
        currentSuggestionIndex.value - 1,
        -1
      )
      break
      
    case 'Enter':
      event.preventDefault()
      if (currentSuggestionIndex.value >= 0) {
        const selectedSuggestion = keywordSuggestions.value[currentSuggestionIndex.value]
        selectSuggestion(selectedSuggestion?.text || '')
      } else {
        applyKeywordSearch()
      }
      break
      
    case 'Escape':
      showSuggestions.value = false
      currentSuggestionIndex.value = -1
      break
      
    case 'Tab':
      // Autocomplete with the first suggestion
      if (currentSuggestionIndex.value < 0 && keywordSuggestions.value.length > 0) {
        event.preventDefault()
        const firstSuggestion = keywordSuggestions.value[0]
        autocompleteWithSuggestion(firstSuggestion?.text || '')
      }
      break
  }
}

// Autocomplete functionality
const autocompleteWithSuggestion = (suggestionText: string) => {
  const currentValue = searchKeyword.value.toLowerCase()
  const suggestionLower = suggestionText.toLowerCase()
  
  // Only autocomplete if suggestion starts with current input
  if (suggestionLower.startsWith(currentValue) && currentValue.length > 0) {
    isAutocompleting.value = true
    searchKeyword.value = suggestionText
    
    // Select the autocompleted part
    nextTick(() => {
      const input = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement
      if (input) {
        input.setSelectionRange(currentValue.length, suggestionText.length)
      }
      isAutocompleting.value = false
    })
  }
}

const handleSearchBlur = () => {
  // Delay hiding suggestions to allow for clicks
  setTimeout(() => {
    showSuggestions.value = false
  }, 200)
}

const clearSearchWithSuggestions = () => {
  searchKeyword.value = ''
  clearSuggestions()
  showSuggestions.value = false
  
  // Clear in front page store
  clearFilter('search_keywords')
  
  // Clear in vehicles store by applying empty filter
  const vehiclesStore = useVehiclesStore()
  vehiclesStore.applyFilters({
    ...vehiclesStore.activeFilters,
    search_keywords: undefined,
  })
  
  // Update URL to remove search_keywords parameter
  if (process.client) {
    const router = useRouter()
    const route = useRoute()
    const newQuery = { ...route.query }
    delete newQuery.search_keywords
    router.replace({ query: newQuery })
  }
}

const selectSuggestion = (suggestionText: string) => {
  // Use the enhanced selectSuggestion method from composable
  const suggestion = keywordSuggestions.value.find(s => s.text === suggestionText)
  if (suggestion) {
    const selectedText = (useKeywordSuggestions() as any).selectSuggestion(suggestion)
    searchKeyword.value = selectedText
    updateFilter('search_keywords', selectedText)
  } else {
    searchKeyword.value = suggestionText
    updateFilter('search_keywords', suggestionText)
  }
  showSuggestions.value = false
}

const getSuggestionCategoryColor = (category: string) => {
  const colors = {
    make: 'bg-blue-500',
    model: 'bg-green-500', 
    feature: 'bg-purple-500',
    color: 'bg-pink-500',
    bodyType: 'bg-orange-500',
    fuelType: 'bg-yellow-500',
    synonym: 'bg-gray-500'
  }
  return colors[category as keyof typeof colors] || 'bg-gray-500'
}

const getSuggestionCategoryLabel = (category: string) => {
  const labels = {
    make: 'Make',
    model: 'Model',
    feature: 'Feature', 
    color: 'Color',
    bodyType: 'Type',
    fuelType: 'Fuel',
    synonym: 'Related',
    price: 'Price',
    year: 'Year',
    popular: 'Popular',
    recent: 'Recent'
  }
  return labels[category as keyof typeof labels] || category
}

const getSuggestionCategoryStyle = (category: string) => {
  const styles = {
    make: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    model: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    feature: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    color: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
    bodyType: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
    fuelType: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
    synonym: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
    price: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
    year: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
    popular: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
    recent: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
  }
  return styles[category as keyof typeof styles] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
}

const getIconComponentForSuggestion = (suggestion: any) => {
  const categoryIcons = {
    make: Car,
    model: Car,
    bodyType: Truck,
    color: Palette,
    fuelType: Fuel,
    feature: Settings,
    price: DollarSign,
    year: Calendar,
    recent: Clock,
    popular: TrendingUp,
    synonym: Link,
    transmission: Settings,
    drivetrain: Settings,
    fuel: Fuel,
    seats: Users,
    doors: MapPin,
    badge: Award
  }
  return categoryIcons[suggestion.category as keyof typeof categoryIcons] || Search
}

// Access composable properties
const keywordSuggestionsComposable = useKeywordSuggestions() as any
const hasRecentSearches = keywordSuggestionsComposable.hasRecentSearches
const hasPopularSearches = keywordSuggestionsComposable.hasPopularSearches
const clearRecentSearches = keywordSuggestionsComposable.clearRecentSearches

// Initialize useForm to provide context for FormField
useForm()

// Define badge filter types
interface Badge {
value: string;
displayValue: string;
count: number;
}

// Mobile drawer state
const isMobileFiltersOpen = ref(false)
const mobileSectionsOpen = ref({
body: true, // Open by default
make: false,
model: false,
badge: false,
})

// Filter dialog state
const isFilterDialogOpen = ref(false)

// Get query string for navigation
const currentFilterQuery = computed(() => frontPageStore.currentFilterQuery)
const navSearchFilteredCount = computed(() => frontPageStore.navSearchFilteredCount)

// Get total count of active filters
const activeFiltersCount = computed(() => {
let count = 0;

// Count array-based filters (each array item counts as 1)
if (Array.isArray(frontPageStore.frontPageFilters.body) && frontPageStore.frontPageFilters.body.length > 0) {
  count += frontPageStore.frontPageFilters.body.length;
}

if (Array.isArray(frontPageStore.frontPageFilters.make) && frontPageStore.frontPageFilters.make.length > 0) {
  count += frontPageStore.frontPageFilters.make.length;
}

if (Array.isArray(frontPageStore.frontPageFilters.model) && frontPageStore.frontPageFilters.model.length > 0) {
  count += frontPageStore.frontPageFilters.model.length;
}

if (Array.isArray(frontPageStore.frontPageFilters.badge) && frontPageStore.frontPageFilters.badge.length > 0) {
  count += frontPageStore.frontPageFilters.badge.length;
}

if (Array.isArray(frontPageStore.frontPageFilters.condition) && frontPageStore.frontPageFilters.condition.length > 0) {
  count += frontPageStore.frontPageFilters.condition.length;
}

// Count string-based filters (each non-empty string counts as 1)
if (frontPageStore.frontPageFilters.search_keywords && frontPageStore.frontPageFilters.search_keywords.trim() !== '') {
  count += 1;
}

// Count range-based filters if min/max values are set and different from defaults
if (frontPageStore.frontPageFilters.perweek && 
    typeof frontPageStore.frontPageFilters.perweek === 'object') {
  const { min, max } = frontPageStore.frontPageFilters.perweek;
  // Use the store's defaultPerWeekRange for accurate comparison
  const defaultMin = frontPageStore.defaultPerWeekRange.min;
  const defaultMax = frontPageStore.defaultPerWeekRange.max;
  if ((min !== undefined && min !== defaultMin) || 
      (max !== undefined && max !== defaultMax)) {
    count += 1;
  }
}

return count;
});

// Testing mode flag
const isTestMode = ref(false);

// Search keyword with debounce
const searchKeyword = ref('')
const modelSearchKeyword = ref('')
let searchTimeout: NodeJS.Timeout | null = null
let modelSearchTimeout: NodeJS.Timeout | null = null

// Dropdown state
const expandedMakes = ref<string[]>([])
// Track models that should appear at the top of their lists
const topModels = ref<string[]>([])

// Toggle expanded state for a make group (allows multiple)
const toggleMakeExpanded = (makeName: string) => {
const index = expandedMakes.value.indexOf(makeName);
if (index > -1) {
  // If already expanded, remove it (collapse)
  expandedMakes.value.splice(index, 1);
} else {
  // If not expanded, add it (expand)
  expandedMakes.value.push(makeName);
}
}

// Auto-expand makes when they're selected
watch(() => frontPageStore.frontPageFilters.make, (newValue) => {
if (Array.isArray(newValue) && newValue.length > 0) {
  // Clear existing expanded makes
  expandedMakes.value = [];
  
  // Expand each selected make
  newValue.forEach(makeValue => {
    if (typeof makeValue === 'string') {
      const make = makes.value.find(m => m.value === makeValue);
      if (make && typeof make.displayValue === 'string') {
        expandedMakes.value.push(make.displayValue);
      }
    }
  });
}
}, { immediate: true });

// Expand the make group that contains the currently selected model
const expandMakeOfSelectedModel = () => {
const selectedModel = frontPageStore.frontPageFilters.model?.[0]
// Safety check to ensure selectedModel is a string before trying to split it
if (!selectedModel || typeof selectedModel !== 'string') return

// Model values are stored as "make_model" format
const modelParts = selectedModel.split('_')
if (modelParts.length < 2) return

const makeValue = modelParts[0]

// Ensure makeValue is a string before using it
if (typeof makeValue !== 'string') return;

// Find the make in our makes list that matches this model's make
// Add safety check for m.value type
const make = makes.value.find((m: FilterOption) => 
  typeof m?.value === 'string' && m.value.toLowerCase() === makeValue.toLowerCase()
)

if (make && typeof make.displayValue === 'string' && !expandedMakes.value.includes(make.displayValue)) {
  // Add this make to the expanded list
  expandedMakes.value.push(make.displayValue)
}
}

// Check if a make group is expanded
const isGroupExpanded = (makeName: string) => {
return expandedMakes.value.includes(makeName)
}

// Function to find a make's display value from its value
const getMakeDisplayValue = (makeValue: string) => {
// Ensure makeValue is a string
if (typeof makeValue !== 'string') return makeValue; // Return original value if not string
// Add safety check for m.value type and makeValue type before comparing lowercase
const make = makes.value.find((m: FilterOption) => 
  typeof m?.value === 'string' && typeof makeValue === 'string' && m.value.toLowerCase() === makeValue.toLowerCase()
)
return make?.displayValue || makeValue // Fallback to value if not found
}

// Setup and clean up event listeners
onMounted(async () => {
// **FIRST: Check URL query parameters before store initialization**
const route = useRoute()
let urlSearchKeywords = ''
if (route.query.search_keywords && typeof route.query.search_keywords === 'string') {
  urlSearchKeywords = route.query.search_keywords.trim()
}

// Initialize data from frontPageStore if needed
await frontPageStore.initializeFrontPage();

// **SECOND: Set search keyword from URL (takes priority) or store**
if (urlSearchKeywords) {
  // URL takes priority - update both the local value and store
  searchKeyword.value = urlSearchKeywords
  frontPageStore.updateFilter('search_keywords', urlSearchKeywords)
} else {
  // Fallback to store value
  searchKeyword.value = frontPageStore.frontPageFilters.search_keywords || ''
}

// Only expand makes if a model is already selected
if (frontPageStore.frontPageFilters.model.length > 0) {
  // Try to find the make for the selected model and expand it
  expandMakeOfSelectedModel();
} else {
  // Keep expandedMakes empty if no model is selected to show all models
  expandedMakes.value = [];
}
})

// Watch for changes in model search keyword
watch(modelSearchKeyword, (newValue, oldValue) => {
// Skip if the value is empty and was previously empty too
if (!newValue.trim() && !oldValue?.trim()) {
  return;
}
})

// Watch for changes in the main search keyword
watch(searchKeyword, (newValue, oldValue) => {
// removed log statement
});

onBeforeUnmount(() => {
if (searchTimeout) clearTimeout(searchTimeout)
if (modelSearchTimeout) clearTimeout(modelSearchTimeout)
})

// Apply keyword search directly
const applyKeywordSearch = () => {
// removed log statement
updateFilter('search_keywords', searchKeyword.value);
}

// Debounce search input
const debounceSearch = () => {
if (searchTimeout) clearTimeout(searchTimeout)
searchTimeout = setTimeout(() => {
  // removed log statement
  updateFilter('search_keywords', searchKeyword.value)
}, 300)
}

// Debounce model search input
const debounceModelSearch = () => {
if (modelSearchTimeout) clearTimeout(modelSearchTimeout);

const keyword = modelSearchKeyword.value?.trim();
// Only set timeout if keyword is not empty
if (keyword) {
  modelSearchTimeout = setTimeout(() => {
    handleModelSearch();
  }, 300);
} else {
}
}

// Define filter value types based on store filter names
type FilterValueMap = {
search_keywords: string;
body: string[];
make: string[];
model: string[];
badge: string[]; // Add badge to filter types
condition: string[];
perweek: { min: number; max: number };
}

type FilterName = keyof FilterValueMap
type FilterValue = string | string[] | { min: number; max: number }

// Align with store's expected FilterValue type
interface StoreFilterValue {
type: 'string' | 'array' | 'range';
value: string | string[] | { min: number; max: number };
}

// Type guard for filter values
const isValidFilterValue = <T extends keyof FilterValueMap>(
value: unknown, 
filterName?: T
): value is FilterValueMap[T] => {
// Special case for search_keywords which only accepts strings
if (filterName === 'search_keywords') {
  return typeof value === 'string'
}

// badges are strings arrays like models and makes
if (filterName === 'badge') {
  return Array.isArray(value) && value.every(item => typeof item === 'string')
}

return typeof value === 'string' || 
       (Array.isArray(value) && value.every(item => typeof item === 'string')) ||
       (typeof value === 'object' && value !== null && 
        'min' in value && 'max' in value &&
        typeof value.min === 'number' && typeof value.max === 'number')
}

// Update filter and propagate to stores with type safety
const updateFilter = <T extends FilterName>(
filterName: T,
value: FilterValueMap[T]
) => {
// Store previous values to check if clearing is necessary
const prevBody = [...frontPageStore.frontPageFilters.body];
const prevMake = [...frontPageStore.frontPageFilters.make];
const prevModel = [...frontPageStore.frontPageFilters.model];

// Check for search keywords validity first
if (filterName === 'search_keywords') {
  if (typeof value !== 'string') {
    console.warn(`[NavSearchFilters] Invalid value type for search_keywords: ${typeof value}`);
    return; // Prevent updating if type is wrong
  }
}

// Basic type validation for other filters
if (!isValidFilterValue(value, filterName)) {
   console.error(`[NavSearchFilters] Invalid filter value type for ${filterName}`);
   return;
}

try {
  // --- Step 1: Update the primary filter in the store ---
  if (filterName === 'search_keywords') {
    frontPageStore.updateFilter(filterName, value);
  } else if (Array.isArray(value)) {
    // PASS THE RAW ARRAY DIRECTLY
    frontPageStore.updateFilter(filterName, value);
  } else if (filterName === 'perweek' && typeof value === 'object' && value !== null && 'min' in value) { // Explicitly check for range object
    // STILL PASS THE OBJECT FOR RANGE
    frontPageStore.updateFilter(filterName, value);
  } else {
    // Handle potential errors or unexpected types
    console.error(`[NavSearchFilters] Unexpected value type or filter name for update: filter=${filterName}, value=`, value);
    return; // Prevent updating with invalid data
  }
  
  // --- Step 2: Check dependencies and clear if necessary ---
  let shouldClearMake = false;
  let shouldClearModel = false;

  // ** Body changed: Check if Make/Model need clearing **
  if (filterName === 'body' && JSON.stringify(value) !== JSON.stringify(prevBody)) {
    const currentMakeSelection = frontPageStore.frontPageFilters.make;
    const currentModelSelection = frontPageStore.frontPageFilters.model;
    
    // Get the NEWLY available makes based on the NEW body selection
    const newAvailableMakes = makes.value; // relies on makes computed property reacting fast enough
    const newAvailableMakeValues = new Set(newAvailableMakes.map(m => m.value));

    // Check if any currently selected MAKE is now invalid
    if (currentMakeSelection.length > 0 && !currentMakeSelection.every(m => newAvailableMakeValues.has(m))) {
       shouldClearMake = true;
       shouldClearModel = true; // If make is invalid, model must also be cleared
    } else {
       // If Make is still valid, check if Model is still valid
       const newAvailableModels = models.value; // relies on models computed property reacting fast enough
       const newAvailableModelValues = new Set(newAvailableModels.map(m => m.value));
       if (currentModelSelection.length > 0 && !currentModelSelection.every(m => newAvailableModelValues.has(m))) {
          shouldClearModel = true;
       }
    }
  } 
  // ** Make changed: Handle model selection differently when adding vs removing makes **
  else if (filterName === 'make' && JSON.stringify(value) !== JSON.stringify(prevMake)) {
     const currentModelSelection = frontPageStore.frontPageFilters.model;
     
     // Don't clear models when unchecking a make (when removing items from make selection)
     // Only check for clearing models when ADDING a make or replacing the make selection entirely
     
     // Check if we're removing makes (the new make list is shorter than previous)
     const isRemovingMakes = Array.isArray(value) && Array.isArray(prevMake) && value.length < prevMake.length;
     
     if (!isRemovingMakes && currentModelSelection.length > 0) {
       // Get the NEWLY available models based on the NEW make selection
       const newAvailableModels = models.value; // relies on models computed property reacting fast enough
       const newAvailableModelValues = new Set(newAvailableModels.map(m => m.value));

       // Check if any currently selected MODEL is now invalid
       if (!currentModelSelection.every(m => newAvailableModelValues.has(m))) {
          shouldClearModel = true;
       }
     }
  }

  // --- Step 3: Apply dependent clears in the store ---
  if (shouldClearModel) {
     frontPageStore.clearFilter('model');
     modelSearchKeyword.value = ''; // Clear search when model is cleared programmatically
  }
  if (shouldClearMake) {
     frontPageStore.clearFilter('make');
     // Model is already cleared if make was cleared
  }
  
  // --- Step 4: Update UI state based on the final filter state ---
  
  // Mobile section visibility updates
  if (filterName === 'body') {
      mobileSectionsOpen.value.make = true; // Always open make after body change
      mobileSectionsOpen.value.model = !shouldClearModel; // Keep model open only if it wasn't cleared
  } else if (filterName === 'make') {
      mobileSectionsOpen.value.model = true; // Always open model after make change/clear
  }
  
  // Desktop popover expansion updates (`expandedMakes`)
  if (filterName === 'body') {
      if (shouldClearMake) {
          // Reset completely if make was cleared
           expandedMakes.value = (makes.value.length > 0 && makes.value[0] && typeof makes.value[0].displayValue === 'string') ? [makes.value[0].displayValue] : [];
      } 
      // else: Don't change expansion if make wasn't cleared
  } else if (filterName === 'make') {
      // Read the *final* make state after potential clearing
      const finalMakeFilter = frontPageStore.frontPageFilters.make; 
      if (Array.isArray(finalMakeFilter) && finalMakeFilter.length === 1 && typeof finalMakeFilter[0] === 'string') {
        // If exactly one make is selected, expand it
        const displayValue = getMakeDisplayValue(finalMakeFilter[0]);
        expandedMakes.value = displayValue ? [displayValue] : [];
      } else {
        // If multiple makes selected or make is cleared, reset to default/first
         expandedMakes.value = (makes.value.length > 0 && makes.value[0] && typeof makes.value[0].displayValue === 'string') ? [makes.value[0].displayValue] : [];
      }
  } else if (filterName === 'model') {
      // Clear model search keyword (handled by handlePostModelSelect called via toggleFilterItem)
      // Make expansion is also handled by handlePostModelSelect
       modelSearchKeyword.value = ''; // Explicitly clear here too for safety
  }


} catch (error) {
   console.error(`[NavSearchFilters] Error updating filter '${filterName}':`, error);
}
}

// Restore clearFilter function definition (without sync logic)
const clearFilter = (filterName: string) => {
if (!(filterName in frontPageStore.frontPageFilters)) {
  return
}

// Ensure filterName is a keyof FrontPageFilters before proceeding
const validFilterName = filterName as keyof FrontPageFilters; 

try {
  // --- Dependency Handling on Clear ---
  let shouldClearModel = false;
  let shouldClearMake = false;
  let shouldClearBadge = false;

  if (validFilterName === 'body') {
    shouldClearMake = true;
    shouldClearModel = true;
    shouldClearBadge = true;
    // Empty expandedMakes array to show all models
    expandedMakes.value = [];
    mobileSectionsOpen.value.make = false; // Close dependent sections on mobile
    mobileSectionsOpen.value.model = false;
  }
  if (validFilterName === 'make') {
    shouldClearModel = true;
    shouldClearBadge = true;
    // Empty the expandedMakes array to show the "all models" view
    expandedMakes.value = []; 
    mobileSectionsOpen.value.model = false; // Close model section on mobile
  }
  if (validFilterName === 'model') {
    shouldClearBadge = true;
    modelSearchKeyword.value = ''; // Clear model search keyword
    // Reset expanded makes to show all models
    expandedMakes.value = [];
  }

  // Clear in front page store first
  frontPageStore.clearFilter(validFilterName);

  // Apply dependent clears in frontPageStore
  if (shouldClearModel) frontPageStore.clearFilter('model');
  if (shouldClearMake) frontPageStore.clearFilter('make');
  if (shouldClearBadge) frontPageStore.clearFilter('badge');

} catch (error) {
  console.error(`[NavSearchFilters] Error clearing filter '${filterName}':`, error);
}
}

// Clear all filters
const clearAllFilters = () => {
// Use the existing clearFilter logic for each filter to handle dependencies
clearFilter('body'); // This will clear body, make, model, badge
clearFilter('search_keywords'); // Clear keywords separately
clearFilter('badge'); // Ensure badges are cleared

// Clear expanded makes to show all models view
expandedMakes.value = [];
}

// Computed values for UI state
const hasActiveFilters = computed(() => {
return (
  frontPageStore.frontPageFilters.body.length > 0 ||
  frontPageStore.frontPageFilters.make.length > 0 ||
  frontPageStore.frontPageFilters.model.length > 0 ||
  frontPageStore.frontPageFilters.condition.length > 0 ||
  !!frontPageStore.frontPageFilters.search_keywords
)
})

// Format active filters for display
const activeFilters = computed(() => {
const result: Record<string, { label: string; value: string }> = {}

// Add body filter
if (frontPageStore.frontPageFilters.body.length > 0) {
  const selectedBodies = frontPageStore.frontPageFilters.body;
  let displayValue = '';
  if (selectedBodies.length === 1) {
    const bodyOption = bodyTypes.value.find((opt: FilterOption) => opt.value === selectedBodies[0]);
    displayValue = bodyOption?.displayValue ? String(bodyOption.displayValue) : (selectedBodies[0] || '');
  } else {
    displayValue = `Multiple (${selectedBodies.length})`;
  }
  result.body = { label: 'Type', value: displayValue };
}

// Add make filter
if (frontPageStore.frontPageFilters.make.length > 0) {
  const selectedMakes = frontPageStore.frontPageFilters.make;
  let displayValue = '';
  if (selectedMakes.length === 1) {
    const makeOption = makes.value.find((opt: FilterOption) => opt.value === selectedMakes[0]);
    displayValue = makeOption?.displayValue ? String(makeOption.displayValue) : (selectedMakes[0] || '');
  } else {
    displayValue = `Multiple (${selectedMakes.length})`;
  }
   result.make = { label: 'Make', value: displayValue };
}

// Add model filter
if (frontPageStore.frontPageFilters.model.length > 0) {
   const selectedModels = frontPageStore.frontPageFilters.model;
   let displayValue = '';
   if (selectedModels.length === 1) {
           displayValue = getModelDisplayName(selectedModels[0]) || '';
   } else {
     displayValue = `Multiple (${selectedModels.length})`;
   }
   if (displayValue) { // Ensure we have a value before adding
     result.model = { label: 'Model', value: displayValue };
   }
}

// Add search filter
if (frontPageStore.frontPageFilters.search_keywords) {
  result.search = {
    label: 'Search',
    value: frontPageStore.frontPageFilters.search_keywords
  }
}

return result
})

// Computed properties for filter options
const bodyTypes = computed(() => frontPageStore.filterOptions.bodyTypes || [])
const bodyTypesCount = computed(() => bodyTypes.value.length)

// CHAINED FILTERING: Makes available based on selected Body Type(s)
const makes = computed(() => {
const allMakesOptions = frontPageStore.filterOptions.makes || [];
const selectedBodyTypes = frontPageStore.frontPageFilters.body || [];
const allVehicles = vehiclesStore.vehicles || [];

// If no body types selected, return all makes that have at least one vehicle
if (!Array.isArray(selectedBodyTypes) || selectedBodyTypes.length === 0) {
  return allMakesOptions.filter(make => 
    make.value && // Ensure make has a value
    allVehicles.some(vehicle => vehicle?.make?.value?.[0] === make.value)
  );
}

// Filter makes: only include makes that have vehicles matching ANY of the selected body types
const availableMakeValues = new Set<string>();
allVehicles.forEach(vehicle => {
  const vehicleMake = vehicle?.make?.value?.[0];
  const vehicleBody = vehicle?.body?.value?.[0];
  if (vehicleMake && vehicleBody && selectedBodyTypes.includes(vehicleBody)) {
    availableMakeValues.add(vehicleMake);
  }
});

return allMakesOptions.filter(make => make.value && availableMakeValues.has(make.value));
})

const makesCount = computed(() => makes.value.length)

// CHAINED FILTERING: Models available based on selected Body Type(s) AND selected Make(s)
const models = computed(() => {
const allModelOptions = frontPageStore.filterOptions.models || [];
const selectedBodyTypes = frontPageStore.frontPageFilters.body || [];
const selectedMakes = frontPageStore.frontPageFilters.make || []; // Now potentially multiple
const selectedModels = frontPageStore.frontPageFilters.model || []; // Get currently selected models
const allVehicles = vehiclesStore.vehicles || [];

// Filter vehicles based on selected body types (if any)
const vehiclesFilteredByBody = (!Array.isArray(selectedBodyTypes) || selectedBodyTypes.length === 0)
  ? allVehicles
  : allVehicles.filter(vehicle => {
      const vehicleBody = vehicle?.body?.value?.[0];
      return vehicleBody && selectedBodyTypes.includes(vehicleBody);
    });

// Filter vehicles further based on selected makes (if any)
const vehiclesFilteredByMakeAndBody = (!Array.isArray(selectedMakes) || selectedMakes.length === 0)
  ? vehiclesFilteredByBody // If no makes selected, use body-filtered vehicles
  : vehiclesFilteredByBody.filter(vehicle => {
      const vehicleMake = vehicle?.make?.value?.[0];
      // Check if the vehicle's make is included in the list of selected makes
      return vehicleMake && selectedMakes.includes(vehicleMake);
    });

// Get unique model values from the finally filtered vehicles
const availableModelValues = new Set<string>();
vehiclesFilteredByMakeAndBody.forEach(vehicle => {
  const vehicleModel = vehicle?.model?.value?.[0];
  if (vehicleModel) {
    availableModelValues.add(vehicleModel);
  }
});

// Also add any currently selected models to ensure they remain visible
// even if their make is no longer selected
if (Array.isArray(selectedModels)) {
  selectedModels.forEach(modelValue => {
    if (modelValue) availableModelValues.add(modelValue);
  });
}

// Return the model options that match the available model values
return allModelOptions.filter(model => model.value && availableModelValues.has(model.value));
})

const modelsCount = computed(() => models.value.length)

// Computed property for models grouped by make, with selected model sorted to top
const modelsForExpandedMakes = computed(() => {
const groupedModels: Record<string, FilterOption[]> = {};
const availableModels = models.value; // Already filtered by body/make
const vehiclesStore = useVehiclesStore(); // Assuming it's needed for matching
const selectedModelValue = frontPageStore.frontPageFilters.model?.[0]; // Get the first selected model

makes.value.forEach((make: FilterOption) => {
  if (typeof make.value !== 'string') return;

  const makeValue = make.value;
  const makeKey = makeValue.toLowerCase(); // Use lowercase for consistency
  let modelsForThisMake: FilterOption[] = [];

  // Find all models associated with this make from the available models list
  availableModels.forEach((model: FilterOption) => {
    if (typeof model.value !== 'string') return;

    // Simple check: Does the model value start with the make key?
    // Assumes model.value format is "make_model"
    if (model.value.toLowerCase().startsWith(makeKey + '_')) {
       // Additional check: Ensure a vehicle exists with this specific make AND model combination
       // This prevents showing models that *could* belong but don't exist in the current vehicle set for this make.
        const hasMatchingVehicle = vehiclesStore.vehicles.some((vehicle: any) =>
          vehicle.model?.value?.[0] === model.value &&
          vehicle.make?.value?.[0] === makeValue // Use original makeValue here
        );

        if (hasMatchingVehicle) {
            modelsForThisMake.push(model);
        }
    }
     // Fallback check (if format isn't guaranteed "make_model")
     // Find if any vehicle explicitly links this model to this make
     else if (vehiclesStore.vehicles.some((vehicle: any) =>
                vehicle.model?.value?.[0] === model.value &&
                vehicle.make?.value?.[0] === makeValue // Use original makeValue
            ))
     {
         // Avoid duplicates if already added by prefix check
         if (!modelsForThisMake.some(m => m.value === model.value)) {
             modelsForThisMake.push(model);
         }
     }
  });

  // Sort: Place the selected model at the top if it belongs to this make
  if (selectedModelValue && typeof selectedModelValue === 'string') {
    // Check if the selected model actually belongs to this make's list
    const selectedModelIndex = modelsForThisMake.findIndex(m => m.value === selectedModelValue);

    if (selectedModelIndex > -1) { // If found in this make's list
      const selectedModelOption = modelsForThisMake.splice(selectedModelIndex, 1)[0]; // Remove and get the model
             if (selectedModelOption) {
         modelsForThisMake.unshift(selectedModelOption); // Add it to the beginning
       }
    }
  }

  // Optional: Add secondary sort (e.g., alphabetical) for remaining models
  // modelsForThisMake.sort((a, b) => { /* ... */ });

  groupedModels[makeKey] = modelsForThisMake;
});

return groupedModels;
});

// Globally filtered models (for search)
const globallyFilteredModels = computed(() => {
// Start with all models that have counts > 0
let filteredModels = models.value; // This will already respect body & make filters

// If keyword is empty, return all models immediately
const keyword = modelSearchKeyword.value.trim().toLowerCase();
if (!keyword) {
  return filteredModels;
}

// Filter models based on search keyword
filteredModels = filteredModels.filter((model: FilterOption) => {
  if (!model) return false;
  
  // Match against display value
  const displayValue = String(model.displayValue || '').toLowerCase();
  // Match against value (model code)
  const modelValue = typeof model.value === 'string' ? model.value.toLowerCase() : String(model.value || '').toLowerCase();
  
  // Get make name from value if it's in make_model format
  let makeNameFromValue = '';
  let modelCodeOnly = modelValue;
  if (typeof modelValue === 'string' && modelValue.includes('_')) {
    const parts = modelValue.split('_');
    makeNameFromValue = parts[0] || '';
    modelCodeOnly = parts[1] || '';
  }
  
  // Try to find make display name from the make value
  let makeDisplayName = '';
  if (makeNameFromValue) {
    const makeOption = frontPageStore.filterOptions.makes?.find(
      (make: FilterOption) => {
        if (!make || typeof make.value !== 'string') return false;
        return make.value === makeNameFromValue;
      }
    );
    if (makeOption) {
      makeDisplayName = String(makeOption.displayValue || '').toLowerCase();
    }
  }
  
  // For combined make + model search like "bmw x5"
  const combinedSearch = `${makeDisplayName} ${displayValue}`.toLowerCase();
  
  // Check if keyword matches any part
  const matchesDisplayValue = displayValue.includes(keyword);
  const matchesModelValue = modelValue.includes(keyword);
  const matchesModelCodeOnly = modelCodeOnly.includes(keyword);
  const matchesMakeDisplayName = makeDisplayName.includes(keyword);
  const matchesCombined = combinedSearch.includes(keyword);
  
  return matchesDisplayValue || matchesModelValue || matchesModelCodeOnly || 
         matchesMakeDisplayName || matchesCombined;
});

// Add displayMake property to models if not already present
filteredModels.forEach((model: any) => {
  if (!model.displayMake) {
    model.displayMake = getModelMakeName(model.value);
  }
});

// Sort models: selected models first, then alphabetically by display value
const selectedModels = frontPageStore.frontPageFilters.model || [];
filteredModels.sort((a, b) => {
  // First priority: selected models go to the top
  const aIsSelected = isModelSelected(a.value);
  const bIsSelected = isModelSelected(b.value);
  
  if (aIsSelected && !bIsSelected) return -1;
  if (!aIsSelected && bIsSelected) return 1;
  
  // Second priority: alphabetical sort by display value
  return String(a.displayValue || '').localeCompare(String(b.displayValue || ''));
});

return filteredModels;
})

// Selected values for display
const getSelectedBodyType = computed(() => {
const selected = frontPageStore.frontPageFilters.body;
if (!Array.isArray(selected) || selected.length === 0) return ''; // More robust check
if (selected.length === 1) {
    const bodyType = bodyTypes.value.find((type: FilterOption) => type.value === selected[0])
    return bodyType?.displayValue || selected[0]; // Fallback to value
}
return `Multiple Types (${selected.length})`; // Handle multiple selections
})

const getSelectedMake = computed(() => {
const selected = frontPageStore.frontPageFilters.make;
 if (!Array.isArray(selected) || selected.length === 0) return ''; // More robust check
if (selected.length === 1) {
    const make = makes.value.find((m: FilterOption) => 
      typeof m?.value === 'string' && m.value === selected[0]
    )
    return make?.displayValue || selected[0]; // Fallback to value
}
return `Multiple Makes (${selected.length})`; // Handle multiple selections
})

const getSelectedModel = computed(() => {
const selected = frontPageStore.frontPageFilters.model;
 if (!Array.isArray(selected) || selected.length === 0) return ''; // More robust check
if (selected.length === 1) {
    return getModelDisplayName(selected[0]);
}
return `Multiple Models (${selected.length})`; // Handle multiple selections
})

// Count functions for display
const getBodyTypeCount = (bodyTypeValue: string) => {
try {
  // If the frontPageStore has precalculated counts, use those
  if (frontPageStore.filterCounts?.body?.[bodyTypeValue]) {
    return frontPageStore.filterCounts.body[bodyTypeValue]
  }
  
  // Otherwise calculate from vehicle data
  const allVehicles = vehiclesStore.vehicles || []
  if (!Array.isArray(allVehicles) || allVehicles.length === 0) {
    return Math.floor(Math.random() * 10) + 1 // Fallback for initial load
  }
  
  return allVehicles.filter((vehicle: any) => {
    const vehicleBodyType = vehicle.bodyType || vehicle.body_type || vehicle.body?.value?.[0] || ''
    return vehicleBodyType === bodyTypeValue
  }).length
} catch (error) {
  return 1 // Fallback to non-zero value
}
}

const getMakeCount = (makeValue: string) => {
try {
  // Use pre-calculated counts from frontPageStore
  return frontPageStore.filterCounts?.make?.[makeValue] || 0;
} catch {
  return 0;
}
}

const getModelCount = (modelValue: string) => {
try {
  // Use pre-calculated counts from frontPageStore
  // Model values might be make_model, so we need the exact key
  return frontPageStore.filterCounts?.model?.[modelValue] || 0;
} catch {
  return 0;
}
}

// Utility functions for display
const getModelDisplayName = (modelValue: unknown) => {
try {
  if (!modelValue || typeof modelValue !== 'string') return ''
  
  // Find model in the computed filtered list first for efficiency
  // Check globallyFilteredModels if keyword is active
  if (modelSearchKeyword.value) {
      const model = globallyFilteredModels.value.find((m: FilterOption) => m.value === modelValue);
      if (model) return model.displayValue;
  } else {
      // Otherwise check modelsForExpandedMakes (indirectly via models.value)
      // Iterate through the *values* of the modelsForExpandedMakes object
      for (const makeKey in modelsForExpandedMakes.value) {
          const modelList = modelsForExpandedMakes.value[makeKey];
          const model = modelList?.find((m: FilterOption) => m.value === modelValue);
          if (model) return model.displayValue;
      }
  }

  // Fallback: Check the main filter options if not found above
  const modelFromOptions = frontPageStore.filterOptions.models?.find((m: FilterOption) => m.value === modelValue)
  if (modelFromOptions) return modelFromOptions.displayValue

  // Safely extract model name from model value (remove make prefix)
  if (typeof modelValue === 'string') {
    const parts = modelValue.split('_')
    return parts.length > 1 ? parts[1] : modelValue
  }
  return String(modelValue)
} catch (error) {
  return ''
}
}

// Function to get the make display name from a model value
const getModelMakeName = (modelValue: unknown) => {
try {
  if (!modelValue || typeof modelValue !== 'string') return '';

  // Try to extract make code from model value if it's in make_model format
  let makeCode = '';
  if (typeof modelValue === 'string' && modelValue.includes('_')) {
    makeCode = modelValue.split('_')[0] || '';
  }

  if (!makeCode) return '';

  // Find the make in the filter options
  const makeOption = frontPageStore.filterOptions.makes?.find(
    (make: FilterOption) => {
      if (!make || typeof make.value !== 'string') return false;
      return make.value === makeCode;
    }
  );

  return makeOption?.displayValue || makeCode;
} catch (error) {
  return '';
}
}

const getModelBodyType = (modelValue: unknown) => {
try {
  if (!modelValue || typeof modelValue !== 'string') return ''
  
  let foundModel: FilterOption | undefined;
  // Find model in the computed filtered list first for efficiency
  // Check globallyFilteredModels if keyword is active
  if (modelSearchKeyword.value) {
      foundModel = globallyFilteredModels.value.find((m: FilterOption) => m.value === modelValue);
  } else {
      // Otherwise check modelsForExpandedMakes (indirectly)
      for (const makeKey in modelsForExpandedMakes.value) {
          foundModel = modelsForExpandedMakes.value[makeKey]?.find((m: FilterOption) => m.value === modelValue);
          if (foundModel) break; // Stop searching once found
      }
  }

  // Use displayBody if available on the found model option
  if (foundModel && foundModel.displayBody && foundModel.displayBody.length > 0) {
      return foundModel.displayBody[0];
  }

  // Fallback: Try to find this model in the actual vehicles data
  const allVehicles = vehiclesStore.vehicles || [];
  
  // Find vehicle with this model
  const vehicleWithModel = allVehicles.find((v: any) => { // Renamed variable
    const vehicleMakeValue = (v.make?.value?.[0] || '').toLowerCase();
    const vehicleModelCode = v.model?.value?.[0] || '';
    const combinedVehicleModelValue = `${vehicleMakeValue}_${vehicleModelCode}`; 
    return combinedVehicleModelValue.toLowerCase() === modelValue.toLowerCase(); // Case-insensitive compare
  })

  // If a vehicle was found, extract body type
  if (vehicleWithModel) {
    // Check for displayBody in the model's properties first
    if (vehicleWithModel.model?.displayBody && vehicleWithModel.model.displayBody.length > 0) {
      return vehicleWithModel.model.displayBody[0]
    }
    
    // Try to get body type from the vehicle's body/bodyType properties
    if (vehicleWithModel.body?.displayValue && vehicleWithModel.body.displayValue.length > 0) {
      return vehicleWithModel.body.displayValue[0]
    }
    
    // Fall back to standard body type fields
    return vehicleWithModel.bodyType || vehicleWithModel.body_type || ''
  }

  return '' // Return empty if not found anywhere
} catch (error) {
  return ''
}
}

// Helper functions to check if a filter is selected
const isBodyTypeSelected = (bodyType: string) => {
const selected = frontPageStore.frontPageFilters.body;
// Ensure 'selected' is an array and has items before checking includes
return Array.isArray(selected) && selected.length > 0 && selected.includes(bodyType);
}

const isMakeSelected = (make: string) => {
const selected = frontPageStore.frontPageFilters.make;
return Array.isArray(selected) && selected.length > 0 && selected.includes(make);
}

const isModelSelected = (model: string) => {
const selected = frontPageStore.frontPageFilters.model;
return Array.isArray(selected) && selected.length > 0 && selected.includes(model);
}

// Function to handle model search input (triggered by input/keyup events)
const handleModelSearch = () => {
// No need to do anything if keyword is empty
if (!modelSearchKeyword.value?.trim()) return;

// Expand make groups if needed based on search
if (modelSearchKeyword.value.trim() && globallyFilteredModels.value.length > 0) {
  // Find unique makes from the filtered models
  const foundMakes = new Set()
  globallyFilteredModels.value.forEach((model: FilterOption) => {
    if (model.displayMake) {
      foundMakes.add(model.displayMake)
    }
  })
  
  // Expand these makes
  foundMakes.forEach(makeName => {
    if (typeof makeName === 'string' && !expandedMakes.value.includes(makeName)) {
      expandedMakes.value.push(makeName)
    }
  })
}
}

// Watch active filters to potentially close mobile drawer if needed (optional)
watch(currentFilterQuery, () => {
// Maybe close drawer after applying filter? Or keep open for more changes.
// isMobileFiltersOpen.value = false; 
})

// --- UPDATED: Function to toggle filter items (single for make/model, multi for body) ---
const toggleFilterItem = <T extends 'body' | 'make' | 'model' | 'badge'>(
filterName: T,
itemValue: string
) => {
// Debug toggle action
  

// Ensure strong typing for currentValues to satisfy TS
const currentValues: string[] = Array.isArray(frontPageStore.frontPageFilters[filterName])
  ? [...(frontPageStore.frontPageFilters[filterName] as string[])]
  : [];

const index = currentValues.indexOf(itemValue);
let newValues: string[];
let action: string;

if (index > -1) {
  // Item exists, remove it
  newValues = currentValues.filter((_, i) => i !== index);
  action = 'REMOVED';
} else {
  // Item doesn't exist, add it
  newValues = [...currentValues, itemValue];
  action = 'ADDED';
}

  

// Check if arrays are actually different before updating
if (JSON.stringify(currentValues) !== JSON.stringify(newValues)) {
  
  // Special handling for badge selection - just update the badge filter
  if (filterName === 'badge') {
    // Update badge filter without clearing make
    updateFilter(filterName, newValues);
  }
  // Special handling for models: If selecting a model and no makes are selected, 
  // automatically select the corresponding make
  else if (filterName === 'model' && action === 'ADDED' && frontPageStore.frontPageFilters.make.length === 0) {
    // Extract make from model value (format is usually make_model)
    if (typeof itemValue === 'string' && itemValue.includes('_')) {
      const makeValue = itemValue.split('_')[0];
      if (makeValue) {
        // First update make filter
        updateFilter('make', [makeValue]);
        
        // Then update model filter
        updateFilter(filterName, newValues);
      } else {
        // Fallback if no make could be extracted
        updateFilter(filterName, newValues);
      }
    } else {
      // Fallback for models without make_ prefix
      updateFilter(filterName, newValues);
    }
  } else {
    // Regular update for other filters
    updateFilter(filterName, newValues);
  }

  // Specific logic after model selection
  if (filterName === 'model') {
    handlePostModelSelect(itemValue);
  }
  
  // Auto-expand a make in the Models Filter when it's selected in Makes Filter
  if (filterName === 'make' && action === 'ADDED') {
    // Find the display value for the make
    const makeDisplayValue = getMakeDisplayValue(itemValue);
    if (makeDisplayValue && expandedMakes.value.indexOf(makeDisplayValue) === -1) {
      // Add this make to the expanded list
      expandedMakes.value.push(makeDisplayValue);
    }
  }
}
}

// --- REVISED handleMobileToggleFilter with logging ---
const handleMobileToggleFilter = <T extends 'body' | 'make' | 'model'>(
filterName: T,
itemValue: string,
event: MouseEvent
) => {
 event.stopPropagation();
 event.preventDefault();
 // Reuse the core toggle logic
 toggleFilterItem(filterName, itemValue);
}

// --- REVISED handleMobileClearFilter with expandedMakes reset ---
const handleMobileClearFilter = <T extends 'body' | 'make' | 'model'>(
filterName: T,
event: MouseEvent
) => {
 event.stopPropagation();
 event.preventDefault();
  
 clearFilter(filterName);
 
 // Ensure expandedMakes is reset when clearing models filter
 if (filterName === 'model') {
   expandedMakes.value = [];
 }
}

// --- NEW Helper for post-model selection UI updates ---
const handlePostModelSelect = (selectedModelValue: string) => {
if (typeof selectedModelValue !== 'string') return;

// Clear model search keyword when a model is clicked
modelSearchKeyword.value = '';

// Expand the make of the selected model while maintaining other expanded makes
const modelParts = selectedModelValue.split('_');
if (modelParts.length >= 2) {
  const makeValue = modelParts[0];
  const makeDisplayValue = getMakeDisplayValue(makeValue || '');
  if (makeDisplayValue && expandedMakes.value.indexOf(makeDisplayValue) === -1) {
    // Add the make to the expanded list without replacing the others
    expandedMakes.value.push(makeDisplayValue);
  }
}
}

// Helper function to sort models for the mobile view (selected first)
const sortedMobileModels = (makeKey: string) => {
const modelsList = modelsForExpandedMakes.value[makeKey] || [];
return [...modelsList].sort((a, b) => {
  const aIsSelected = isModelSelected(a.value);
  const bIsSelected = isModelSelected(b.value);
  if (aIsSelected && !bIsSelected) return -1;
  if (!aIsSelected && bIsSelected) return 1;
  // Optional: Add secondary sort if needed, e.g., alphabetical
  // return String(a.displayValue || '').localeCompare(String(b.displayValue || ''));
  return 0; // Keep original order for non-selected items for now
});
};



// Function to get selected model count for a specific make
const getSelectedModelCountForMake = (makeValue: string | unknown): number => {
if (typeof makeValue !== 'string' || !makeValue) return 0;

const selectedModels = frontPageStore.frontPageFilters.model;
if (!Array.isArray(selectedModels) || selectedModels.length === 0) return 0;

const makeKey = makeValue.toLowerCase() + '_'; // Prepare prefix for matching

return selectedModels.filter(modelValue =>
  typeof modelValue === 'string' && modelValue.toLowerCase().startsWith(makeKey)
).length;
};

// Get filters grouped by category
const groupedFilters = computed(() => {
const result: Record<string, any[]> = {}

// Add body filter items
if (frontPageStore.frontPageFilters.body.length > 0) {
  result.body = [...frontPageStore.frontPageFilters.body]
}

// Add make filter items
if (frontPageStore.frontPageFilters.make.length > 0) {
  result.make = [...frontPageStore.frontPageFilters.make]
}

// Add model filter items
if (frontPageStore.frontPageFilters.model.length > 0) {
  result.model = [...frontPageStore.frontPageFilters.model]
}

// Add badge filter items
if (frontPageStore.frontPageFilters.badge && frontPageStore.frontPageFilters.badge.length > 0) {
  result.badge = [...frontPageStore.frontPageFilters.badge]
}

// Add condition filter items
if (frontPageStore.frontPageFilters.condition.length > 0) {
  result.condition = [...frontPageStore.frontPageFilters.condition]
}

// Add search filter (single value)
if (frontPageStore.frontPageFilters.search_keywords) {
  result.search = [frontPageStore.frontPageFilters.search_keywords]
}

return result
})

// Helper to get category label
const getCategoryLabel = (category: string): string => {
const labelMap: Record<string, string> = {
  body: 'Vehicle Type',
  make: 'Make',
  model: 'Model',
  badge: 'Badge',
  condition: 'Condition',
  search: 'Search',
}
return labelMap[category] || category.charAt(0).toUpperCase() + category.slice(1)
}

// Helper to get display value for a filter item
const getDisplayValue = (category: string, value: string): string => {
if (category === 'body') {
  const bodyOption = bodyTypes.value.find((opt: FilterOption) => opt.value === value)
  return bodyOption?.displayValue ? String(bodyOption.displayValue) : value
}

if (category === 'make') {
  const makeOption = makes.value.find((opt: FilterOption) => opt.value === value)
  return makeOption?.displayValue ? String(makeOption.displayValue) : value
}

if (category === 'model') {
  return getModelDisplayName(value) || ''
}

if (category === 'badge') {
  // Find a vehicle with this badge to get the display value
  for (const vehicle of vehiclesStore.vehicles) {
    if (vehicle.badge?.value?.[0] === value && vehicle.badge?.displayValue?.[0]) {
      return vehicle.badge.displayValue[0]
    }
    
    // Also check attributes
    if (Array.isArray(vehicle.attributes)) {
      const badgeAttr = vehicle.attributes.find(attr => 
        attr.Name === 'Badge' && 
        attr.Value && 
        attr.Value.toLowerCase().replace(/\s+/g, '-') === value
      )
      if (badgeAttr?.Value) {
        return badgeAttr.Value
      }
    }
  }
  // Fallback - format the badge value itself
  return value.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
}

return value
}

// Helper to remove a specific item from a filter array
const removeFilterItem = (category: string, item: string) => {
if (category === 'search') {
  clearFilter('search_keywords')
  return
}

// For array-based filters
if (['body', 'make', 'model', 'condition', 'badge'].includes(category)) {
  const filterKey = category as keyof FrontPageFilters
  const filterValue = frontPageStore.frontPageFilters[filterKey]
  
  // Ensure we're working with an array
  if (Array.isArray(filterValue)) {
    const newValues = filterValue.filter(value => value !== item)
    
    // Use typed updateFilter function based on category
    if (category === 'body') {
      updateFilter('body', newValues)
    } 
    else if (category === 'make') {
      updateFilter('make', newValues)
    }
    else if (category === 'model') {
      updateFilter('model', newValues)
    }
    else if (category === 'condition') {
      updateFilter('condition', newValues)
    }
    else if (category === 'badge') {
      updateFilter('badge', newValues)
    }
  }
}
}

// Get models count for a specific make name (display value)
const getModelsCountForMake = (makeName: string): number => {
// Find the make value from display name
const make = makes.value.find((m: FilterOption) => m.displayValue === makeName);
if (!make || typeof make.value !== 'string') return 0;

// Use the pre-calculated counts from frontPageStore
return getMakeCount(make.value);
}

// Get models for a specific make name (display value)
const getModelsForMake = (makeName: string): FilterOption[] => {
// Find the make value from display name
const make = makes.value.find((m: FilterOption) => m.displayValue === makeName);
if (!make || typeof make.value !== 'string') return [];

const makeValue = make.value.toLowerCase();
const modelsList = modelsForExpandedMakes.value[makeValue] || [];

// Only include models with non-zero count
return modelsList.filter(model => getModelCount(model.value) > 0);
}

// Function to get filtered makes
const getFilteredMakes = () => {
// Always show all available makes regardless of what's selected
// This allows users to select additional makes while in the model filter
return makes.value.filter(make => typeof make.value === 'string');
}

// Function to get selected makes
const getSelectedMakes = () => {
const selectedMakes = frontPageStore.frontPageFilters.make;
if (!Array.isArray(selectedMakes) || selectedMakes.length === 0) return [];

return selectedMakes
  .map(makeValue => makes.value.find(make => make.value === makeValue))
  .filter((make): make is FilterOption => make !== undefined);
}

// Function to check if selected makes mode is active
const isSelectedMakesMode = computed(() => {
const selectedMakes = frontPageStore.frontPageFilters.make;
return Array.isArray(selectedMakes) && selectedMakes.length > 0;
})

// Computed property to get the selected badges
const selectedBadges = computed(() => {
return frontPageStore.frontPageFilters.badge || []
})



// Helper function to generate unique badge value
const getUniqueBadgeValue = (modelValue: string, badgeValue: string) => `${modelValue}:${badgeValue}`

const availableBadgesForModel = (modelValue: string) => {
  if (!modelValue || typeof modelValue !== 'string') return []

  // Normalize comparisons to be robust to both "make_model" and "model" formats
  const modelValueLower = modelValue.toLowerCase()
  const parts = modelValueLower.split('_')
  const impliedMakeCode = parts.length > 1 ? parts[0] : ''
  const modelCodeOnly = parts.length > 1 ? parts[1] : modelValueLower

  // Determine which makes to consider
  let makesToConsider: string[] = []
  if (frontPageStore.frontPageFilters.make.length > 0) {
    makesToConsider = frontPageStore.frontPageFilters.make.map(m => String(m).toLowerCase())
  } else if (impliedMakeCode) {
    makesToConsider = [impliedMakeCode]
  } else {
    // Infer makes from vehicles that contain this model
    makesToConsider = Array.from(new Set(
      vehiclesStore.vehicles
        .filter(v => {
          const vMake = String(v?.make?.value?.[0] || '').toLowerCase()
          const vModel = String(v?.model?.value?.[0] || '').toLowerCase()
          return vModel === modelCodeOnly || `${vMake}_${vModel}` === modelValueLower
        })
        .map(v => String(v?.make?.value?.[0] || '').toLowerCase())
        .filter(Boolean)
    ))
  }

  const badgeMap = new Map<string, Badge>()

  makesToConsider.forEach(selectedMakeLower => {
    const vehiclesForMakeModel = vehiclesStore.vehicles.filter(vehicle => {
      const vMake = String(vehicle?.make?.value?.[0] || '').toLowerCase()
      const vModel = String(vehicle?.model?.value?.[0] || '').toLowerCase()
      return (
        vMake === selectedMakeLower &&
        (vModel === modelCodeOnly || `${vMake}_${vModel}` === modelValueLower)
      )
    })

    vehiclesForMakeModel.forEach(vehicle => {
      const baseBadgeValue = String(vehicle.badge?.value?.[0] || '')
      const badgeDisplayValue = String(vehicle.badge?.displayValue?.[0] || '')
      if (baseBadgeValue && badgeDisplayValue) {
        // Keep the original modelValue for the unique key so downstream logic stays consistent
        const uniqueBadgeKey = getUniqueBadgeValue(modelValue, baseBadgeValue)
        if (!badgeMap.has(uniqueBadgeKey)) {
          badgeMap.set(uniqueBadgeKey, {
            value: uniqueBadgeKey,
            displayValue: badgeDisplayValue,
            count: 1
          })
        } else {
          const existing = badgeMap.get(uniqueBadgeKey)!
          existing.count++
        }
      }

      // Also support badges declared in attributes
      if (Array.isArray(vehicle.attributes)) {
        const badgeAttr = vehicle.attributes.find(attr => attr.Name === 'Badge' && attr.Value)
        if (badgeAttr?.Value) {
          const normalized = String(badgeAttr.Value).toLowerCase().replace(/\s+/g, '-')
          const uniqueBadgeKey = getUniqueBadgeValue(modelValue, normalized)
          const displayValueFromAttr = String(badgeAttr.Value)
          if (!badgeMap.has(uniqueBadgeKey)) {
            badgeMap.set(uniqueBadgeKey, {
              value: uniqueBadgeKey,
              displayValue: displayValueFromAttr,
              count: 1
            })
          } else {
            const existing = badgeMap.get(uniqueBadgeKey)!
            existing.count++
          }
        }
      }
    })
  })

  // Fallback: if no badges found with the currently selected make(s),
  // try discovering badges for the model across ALL makes. This handles
  // cases where model values are simple (e.g., "kodiaq") and a make
  // filter might not yet align perfectly with vehicle data.
  if (badgeMap.size === 0) {
    const vehiclesForModel = vehiclesStore.vehicles.filter(vehicle => {
      const vMake = String(vehicle?.make?.value?.[0] || '').toLowerCase()
      const vModel = String(vehicle?.model?.value?.[0] || '').toLowerCase()
      return vModel === modelCodeOnly || `${vMake}_${vModel}` === modelValueLower
    })

    vehiclesForModel.forEach(vehicle => {
      const baseBadgeValue = String(vehicle.badge?.value?.[0] || '')
      const badgeDisplayValue = String(vehicle.badge?.displayValue?.[0] || '')
      if (baseBadgeValue && badgeDisplayValue) {
        const uniqueBadgeKey = getUniqueBadgeValue(modelValue, baseBadgeValue)
        if (!badgeMap.has(uniqueBadgeKey)) {
          badgeMap.set(uniqueBadgeKey, {
            value: uniqueBadgeKey,
            displayValue: badgeDisplayValue,
            count: 1
          })
        } else {
          const existing = badgeMap.get(uniqueBadgeKey)!
          existing.count++
        }
      }

      // Attributes-based badge (e.g., "140TSI Sportline")
      if (Array.isArray(vehicle.attributes)) {
        const badgeAttr = vehicle.attributes.find(attr => attr.Name === 'Badge' && attr.Value)
        if (badgeAttr?.Value) {
          const attrValue = String(badgeAttr.Value)
          const uniqueBadgeKey = getUniqueBadgeValue(modelValue, attrValue.toLowerCase().replace(/\s+/g, '-'))
          const displayValue = attrValue
          if (!badgeMap.has(uniqueBadgeKey)) {
            badgeMap.set(uniqueBadgeKey, {
              value: uniqueBadgeKey,
              displayValue,
              count: 1
            })
          } else {
            const existing = badgeMap.get(uniqueBadgeKey)!
            existing.count++
          }
        }
      }
    })
  }

  return Array.from(badgeMap.values()).sort((a, b) => a.displayValue.localeCompare(b.displayValue))
}

const isBadgeSelected = (badgeUniqueValue: string) => {
return selectedBadges.value.includes(badgeUniqueValue)
}

const toggleBadgeForModel = (modelValue: string, badgeValue: string) => {
const uniqueBadgeValue = getUniqueBadgeValue(modelValue, badgeValue)
toggleFilterItem('badge', uniqueBadgeValue)
}

const clearBadgesForModel = (modelValue: string) => {
frontPageStore.frontPageFilters.badge = frontPageStore.frontPageFilters.badge.filter(badge => !badge.startsWith(`${modelValue}:`))
}
</script>
